import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../App';
import { useOrg } from '../contexts/OrgContext';
import { PageLayout } from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Papa from 'papaparse';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import {
  Building2, Users, RefreshCw, Shield, Plus, Trash2,
  CheckCircle2, XCircle, UserPlus, Search, ScrollText,
  Clock, Link2, Settings, Upload, FileText, Image,
  Briefcase, GraduationCap, MapPin, Phone, Calendar,
  Star, ChevronRight, ChevronDown, Edit2, Save, X,
  Network, BarChart3, UserCheck, PalmtreeIcon, Loader2,
  Merge, Key, Eye, Pencil, Mail, ArrowUpRight, History,
  ShieldCheck, Info, Lock, CalendarDays, AlertTriangle, RotateCcw
} from 'lucide-react';

const API = '/api';

const LEAVE_TYPES = ['annual', 'sick', 'personal', 'parental', 'unpaid', 'other'];
const LEAVE_STATUSES = { pending: 'bg-amber-100 text-amber-700', approved: 'bg-emerald-100 text-emerald-700', rejected: 'bg-red-100 text-red-700' };

// ==================== ORG CHART NODE ====================
const ChartNode = ({ person, people, level = 0, onSelect }) => {
  const [expanded, setExpanded] = useState(level < 2);
  const reports = people.filter(p => p.reports_to === person.user_id);
  return (
    <div className={cn(level > 0 && "ml-8 border-l-2 border-indigo-100 pl-4")}>
      <div className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-accent/50 cursor-pointer transition-colors group"
        onClick={() => onSelect(person)} data-testid={`chart-node-${person.user_id}`}>
        {reports.length > 0 ? (
          <button onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }} className="w-5 h-5 flex items-center justify-center">
            {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
          </button>
        ) : <div className="w-5" />}
        {person.picture ? <img src={person.picture} alt="" className="w-9 h-9 rounded-full border-2 border-white shadow-sm" /> :
          <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-sm font-semibold text-indigo-600 border-2 border-white shadow-sm">
            {(person.name || '?')[0].toUpperCase()}
          </div>}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">{person.name}</p>
          <p className="text-[11px] text-muted-foreground truncate">{person.job_title || person.role}</p>
        </div>
        {person.department && <Badge variant="outline" className="text-[10px] hidden group-hover:inline-flex">{person.department}</Badge>}
        {reports.length > 0 && <Badge variant="secondary" className="text-[10px]">{reports.length}</Badge>}
      </div>
      {expanded && reports.map(r => <ChartNode key={r.user_id} person={r} people={people} level={level + 1} onSelect={onSelect} />)}
    </div>
  );
};

// ==================== PROFILE SECTION HELPERS ====================
const ProfileField = ({ label, value, sub }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-sm py-0.5">
      <span className="text-[10px] text-muted-foreground w-28 flex-shrink-0 pt-0.5">{label}</span>
      <div className="min-w-0">
        <span className="text-xs break-words">{value}</span>
        {sub && <div className="text-[9px] text-muted-foreground/50">{sub}</div>}
      </div>
    </div>
  );
};

const ProfileSection = ({ title, icon: Icon, children }) => {
  const hasContent = React.Children.toArray(children).some(c => c !== null);
  if (!hasContent) return null;
  return (
    <details className="border rounded-lg group/section">
      <summary className="px-3 py-2 text-[11px] font-semibold text-muted-foreground cursor-pointer hover:bg-muted/30 flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3" />}{title}
      </summary>
      <div className="px-3 pb-2 space-y-0.5">{children}</div>
    </details>
  );
};

// ==================== PERSON DETAIL PANEL ====================
const PersonDetail = ({ person, profile, reviews, leave, onClose, onSaveProfile, onAddReview, onAddLeave, onUpdateLeaveStatus, allMembers, departments, onChangeRole, canEdit }) => {
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({});
  const [showAddReview, setShowAddReview] = useState(false);
  const [showAddLeave, setShowAddLeave] = useState(false);
  const [reviewForm, setReviewForm] = useState({ type: 'review', title: '', content: '', rating: null });
  const [leaveForm, setLeaveForm] = useState({ type: 'annual', start_date: '', end_date: '', notes: '' });
  const [detailTab, setDetailTab] = useState('profile');
  const [orgSkills, setOrgSkills] = useState([]);
  const [skillSearch, setSkillSearch] = useState('');
  const [skillDropOpen, setSkillDropOpen] = useState(false);
  const API = '/api';

  useEffect(() => {
    setForm({
      job_title: profile?.job_title || '', department: profile?.department || '',
      skills: profile?.skills || [], start_date: profile?.start_date || '',
      reports_to: profile?.reports_to || '', location: profile?.location || '',
      phone: profile?.phone || '', bio: profile?.bio || '',
      employment_type: profile?.employment_type || 'full-time',
      // Personal
      personal_email: profile?.personal_email || '',
      internal_email: profile?.internal_email || person?.email || '',
      staff_number: profile?.staff_number || '',
      first_names: profile?.first_names || '',
      family_name: profile?.family_name || '',
      preferred_name: profile?.preferred_name || '',
      id_type: profile?.id_type || '',
      id_number: profile?.id_number || '',
      home_address: profile?.home_address || '',
      birthday: profile?.birthday || '',
      phone_whatsapp: profile?.phone_whatsapp || '',
      phone_telegram: profile?.phone_telegram || '',
      messaging_id: profile?.messaging_id || '',
      // Emergency
      emergency_contact_name: profile?.emergency_contact_name || '',
      emergency_contact_relationship: profile?.emergency_contact_relationship || '',
      emergency_contact_phone: profile?.emergency_contact_phone || '',
      // Banking
      bank_name: profile?.bank_name || '',
      account_name: profile?.account_name || '',
      bank_account_number: profile?.bank_account_number || '',
      sort_code: profile?.sort_code || '',
      swift_bic: profile?.swift_bic || '',
      iban: profile?.iban || '',
      bank_address: profile?.bank_address || '',
      // Other
      tshirt_size: profile?.tshirt_size || '',
    });
    setEditMode(false); setDetailTab('profile');
  }, [person?.id, person?.person_id, profile]);

  useEffect(() => {
    (async () => {
      try { const r = await fetch(`${API}/org/skills`, { credentials: 'include' }); if (r.ok) setOrgSkills(await r.json()); } catch {}
    })();
  }, []);

  const handleSave = async () => {
    await onSaveProfile(person.id || person.person_id || person.user_id, {
      ...form, skills: Array.isArray(form.skills) ? form.skills : form.skills.split(',').map(s => s.trim()).filter(Boolean)
    });
    setEditMode(false);
  };

  const addSkill = async (skillName) => {
    const name = skillName.trim();
    if (!name) return;
    const currentSkills = Array.isArray(form.skills) ? form.skills : [];
    if (currentSkills.includes(name)) return;
    setForm(f => ({ ...f, skills: [...currentSkills, name] }));
    // Persist new skill to org library
    if (!orgSkills.includes(name)) {
      try {
        await fetch(`${API}/org/skills`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ name }) });
        setOrgSkills(prev => [...prev, name].sort());
      } catch {}
    }
    setSkillSearch('');
  };

  const removeSkill = (name) => {
    const currentSkills = Array.isArray(form.skills) ? form.skills : [];
    setForm(f => ({ ...f, skills: currentSkills.filter(s => s !== name) }));
  };

  const filteredSkills = orgSkills.filter(s => {
    const currentSkills = Array.isArray(form.skills) ? form.skills : [];
    return s.toLowerCase().includes(skillSearch.toLowerCase()) && !currentSkills.includes(s);
  });

  if (!person) return null;

  return (
    <div className="w-[400px] border-l bg-card flex flex-col flex-shrink-0 h-full" data-testid="person-detail-panel">
      {/* Header */}
      <div className="p-4 border-b flex items-center gap-3">
        {person.picture ? <img src={person.picture} alt="" className="w-12 h-12 rounded-full" /> :
          <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center text-lg font-bold text-indigo-600">
            {(person.name || '?')[0].toUpperCase()}
          </div>}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm truncate">{person.name}</h3>
          <p className="text-xs text-muted-foreground truncate">{person.email}</p>
          {profile?.job_title && <p className="text-xs text-indigo-600 mt-0.5">{profile.job_title}</p>}
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}><X className="w-4 h-4" /></Button>
      </div>

      {/* Sub-tabs */}
      <div className="flex border-b px-2">
        {[{ id: 'profile', label: 'Profile' }, { id: 'performance', label: 'Performance' }, { id: 'leave', label: 'Leave' }].map(t => (
          <button key={t.id} onClick={() => setDetailTab(t.id)}
            className={cn("px-3 py-2 text-xs font-medium border-b-2 transition-colors",
              detailTab === t.id ? "border-indigo-600 text-indigo-600" : "border-transparent text-muted-foreground"
            )}>{t.label}</button>
        ))}
      </div>

      <ScrollArea className="flex-1">
        {detailTab === 'profile' && (
          <div className="p-4 space-y-3">
            <div className="flex justify-end">
              {editMode ? (
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditMode(false)}>Cancel</Button>
                  <Button size="sm" className="h-7 text-xs" onClick={handleSave} data-testid="save-profile-btn"><Save className="w-3 h-3 mr-1" />Save</Button>
                </div>
              ) : canEdit ? <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditMode(true)} data-testid="edit-profile-btn"><Edit2 className="w-3 h-3 mr-1" />Edit</Button> : null}
            </div>
            {editMode ? (
              <div className="space-y-3">
                <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Role</label><Input value={form.job_title} onChange={e => setForm(f => ({...f, job_title: e.target.value}))} className="h-8 text-sm" /></div>
                <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Department</label>
                  <Select value={form.department || '__none__'} onValueChange={v => setForm(f => ({...f, department: v === '__none__' ? '' : v}))}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select department" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {(departments || []).map(d => <SelectItem key={d.department_id || d.name} value={d.name}>{d.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Skills</label>
                  <div className="space-y-1.5">
                    <div className="flex flex-wrap gap-1 min-h-[28px]">
                      {(Array.isArray(form.skills) ? form.skills : []).map(s => (
                        <Badge key={s} variant="secondary" className="text-[10px] pr-0.5 gap-0.5">
                          {s}<button type="button" onClick={() => removeSkill(s)} className="ml-0.5 hover:text-red-500"><X className="w-3 h-3" /></button>
                        </Badge>
                      ))}
                    </div>
                    <div className="relative">
                      <Input value={skillSearch} onChange={e => { setSkillSearch(e.target.value); setSkillDropOpen(true); }}
                        onFocus={() => setSkillDropOpen(true)} onKeyDown={e => { if (e.key === 'Enter' && skillSearch.trim()) { e.preventDefault(); addSkill(skillSearch); setSkillDropOpen(false); } }}
                        placeholder="Search or type to add skill..." className="h-7 text-xs" data-testid="skills-input" />
                      {skillDropOpen && skillSearch && (
                        <div className="absolute z-50 top-full left-0 right-0 bg-card border rounded-md shadow-lg mt-0.5 max-h-32 overflow-y-auto">
                          {filteredSkills.slice(0, 8).map(s => (
                            <button key={s} type="button" className="w-full text-left px-2 py-1 text-xs hover:bg-accent" onClick={() => { addSkill(s); setSkillDropOpen(false); }}>{s}</button>
                          ))}
                          {!filteredSkills.includes(skillSearch.trim()) && skillSearch.trim() && (
                            <button type="button" className="w-full text-left px-2 py-1 text-xs hover:bg-accent text-blue-600 font-medium" onClick={() => { addSkill(skillSearch); setSkillDropOpen(false); }}>
                              + Add &quot;{skillSearch.trim()}&quot;
                            </button>
                          )}
                          {filteredSkills.length === 0 && !skillSearch.trim() && <div className="px-2 py-1 text-xs text-muted-foreground">No matching skills</div>}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Start Date</label><Input type="date" value={form.start_date} onChange={e => setForm(f => ({...f, start_date: e.target.value}))} className="h-8 text-sm font-mono" /></div>
                  <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Employment</label>
                    <Select value={form.employment_type} onValueChange={v => setForm(f => ({...f, employment_type: v}))}>
                      <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="full-time">Full-time</SelectItem><SelectItem value="part-time">Part-time</SelectItem><SelectItem value="contract">Contract</SelectItem><SelectItem value="intern">Intern</SelectItem></SelectContent>
                    </Select></div>
                </div>
                <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Reports To</label>
                  <Select value={form.reports_to || 'none'} onValueChange={v => setForm(f => ({...f, reports_to: v === 'none' ? '' : v}))}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select manager" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">No manager</SelectItem>
                      {allMembers.filter(m => m.id !== person.id && m.id !== person.user_id).map(m => <SelectItem key={m.id || m.email} value={m.id}>{m.name} ({m.email})</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Location</label><Input value={form.location} onChange={e => setForm(f => ({...f, location: e.target.value}))} className="h-8 text-sm" /></div>
                <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Phone</label><Input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} className="h-8 text-sm" /></div>
                <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Bio</label><Textarea value={form.bio} onChange={e => setForm(f => ({...f, bio: e.target.value}))} rows={3} className="text-sm" /></div>

                {/* Comprehensive Fields */}
                <details className="border rounded-lg" open>
                  <summary className="px-3 py-2 text-[11px] font-semibold text-muted-foreground cursor-pointer hover:bg-muted/30 flex items-center gap-1.5"><UserCheck className="w-3 h-3" />Personal Information</summary>
                  <div className="px-3 pb-3 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Staff Number</label><Input value={form.staff_number} onChange={e => setForm(f => ({...f, staff_number: e.target.value}))} className="h-8 text-sm" /></div>
                      <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Preferred Name</label><Input value={form.preferred_name} onChange={e => setForm(f => ({...f, preferred_name: e.target.value}))} className="h-8 text-sm" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">First Name(s)</label><Input value={form.first_names} onChange={e => setForm(f => ({...f, first_names: e.target.value}))} placeholder="As in passport/ID" className="h-8 text-sm" /></div>
                      <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Family Name</label><Input value={form.family_name} onChange={e => setForm(f => ({...f, family_name: e.target.value}))} placeholder="As in passport/ID" className="h-8 text-sm" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Personal Email</label><Input type="email" value={form.personal_email} onChange={e => setForm(f => ({...f, personal_email: e.target.value}))} className="h-8 text-sm" /></div>
                      <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Internal Email</label><Input type="email" value={form.internal_email} onChange={e => setForm(f => ({...f, internal_email: e.target.value}))} className="h-8 text-sm" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">ID Type</label>
                        <Select value={form.id_type || '__none__'} onValueChange={v => setForm(f => ({...f, id_type: v === '__none__' ? '' : v}))}>
                          <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent><SelectItem value="__none__">None</SelectItem><SelectItem value="Passport">Passport</SelectItem><SelectItem value="National ID">National ID</SelectItem><SelectItem value="Drivers License">Drivers License</SelectItem><SelectItem value="Work Permit">Work Permit</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent>
                        </Select></div>
                      <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">ID Number</label><Input value={form.id_number} onChange={e => setForm(f => ({...f, id_number: e.target.value}))} className="h-8 text-sm" /></div>
                    </div>
                    <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Home Address</label><Textarea value={form.home_address} onChange={e => setForm(f => ({...f, home_address: e.target.value}))} rows={2} className="text-sm" /></div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Birthday</label><Input type="date" value={form.birthday} onChange={e => setForm(f => ({...f, birthday: e.target.value}))} className="h-8 text-sm font-mono" /></div>
                      <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">T-Shirt Size</label>
                        <Select value={form.tshirt_size || '__none__'} onValueChange={v => setForm(f => ({...f, tshirt_size: v === '__none__' ? '' : v}))}>
                          <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent><SelectItem value="__none__">None</SelectItem><SelectItem value="XS">XS</SelectItem><SelectItem value="S">S</SelectItem><SelectItem value="M">M</SelectItem><SelectItem value="L">L</SelectItem><SelectItem value="XL">XL</SelectItem><SelectItem value="XXL">XXL</SelectItem></SelectContent>
                        </Select></div>
                    </div>
                  </div>
                </details>

                <details className="border rounded-lg">
                  <summary className="px-3 py-2 text-[11px] font-semibold text-muted-foreground cursor-pointer hover:bg-muted/30 flex items-center gap-1.5"><Phone className="w-3 h-3" />Contact Numbers</summary>
                  <div className="px-3 pb-3 space-y-2">
                    <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">WhatsApp</label><Input value={form.phone_whatsapp} onChange={e => setForm(f => ({...f, phone_whatsapp: e.target.value}))} className="h-8 text-sm" /></div>
                    <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Telegram (Optional)</label><Input value={form.phone_telegram} onChange={e => setForm(f => ({...f, phone_telegram: e.target.value}))} className="h-8 text-sm" /></div>
                    <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Line/Zalo/Telegram ID</label><Input value={form.messaging_id} onChange={e => setForm(f => ({...f, messaging_id: e.target.value}))} className="h-8 text-sm" /></div>
                  </div>
                </details>

                <details className="border rounded-lg">
                  <summary className="px-3 py-2 text-[11px] font-semibold text-muted-foreground cursor-pointer hover:bg-muted/30 flex items-center gap-1.5"><Shield className="w-3 h-3" />Emergency Contact</summary>
                  <div className="px-3 pb-3 space-y-2">
                    <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Full Name</label><Input value={form.emergency_contact_name} onChange={e => setForm(f => ({...f, emergency_contact_name: e.target.value}))} className="h-8 text-sm" /></div>
                    <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Relationship</label><Input value={form.emergency_contact_relationship} onChange={e => setForm(f => ({...f, emergency_contact_relationship: e.target.value}))} className="h-8 text-sm" /></div>
                    <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Phone Number</label><Input value={form.emergency_contact_phone} onChange={e => setForm(f => ({...f, emergency_contact_phone: e.target.value}))} className="h-8 text-sm" /></div>
                  </div>
                </details>

                <details className="border rounded-lg">
                  <summary className="px-3 py-2 text-[11px] font-semibold text-muted-foreground cursor-pointer hover:bg-muted/30 flex items-center gap-1.5"><Building2 className="w-3 h-3" />Banking Details</summary>
                  <div className="px-3 pb-3 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Bank Name</label><Input value={form.bank_name} onChange={e => setForm(f => ({...f, bank_name: e.target.value}))} className="h-8 text-sm" /></div>
                      <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Account Name</label><Input value={form.account_name} onChange={e => setForm(f => ({...f, account_name: e.target.value}))} className="h-8 text-sm" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Account Number</label><Input value={form.bank_account_number} onChange={e => setForm(f => ({...f, bank_account_number: e.target.value}))} className="h-8 text-sm" /></div>
                      <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Sort Code / Routing</label><Input value={form.sort_code} onChange={e => setForm(f => ({...f, sort_code: e.target.value}))} className="h-8 text-sm" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">SWIFT / BIC</label><Input value={form.swift_bic} onChange={e => setForm(f => ({...f, swift_bic: e.target.value}))} className="h-8 text-sm" /></div>
                      <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">IBAN</label><Input value={form.iban} onChange={e => setForm(f => ({...f, iban: e.target.value}))} className="h-8 text-sm" /></div>
                    </div>
                    <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Bank Address</label><Textarea value={form.bank_address} onChange={e => setForm(f => ({...f, bank_address: e.target.value}))} rows={2} className="text-sm" /></div>
                  </div>
                </details>
              </div>
            ) : (
              <div className="space-y-3">
                {[
                  { icon: Briefcase, label: 'Role', value: profile?.job_title },
                  { icon: Building2, label: 'Department', value: profile?.department },
                  { icon: MapPin, label: 'Location', value: profile?.location },
                  { icon: Phone, label: 'Phone', value: profile?.phone },
                  { icon: Calendar, label: 'Start Date', value: profile?.start_date },
                  { icon: UserCheck, label: 'Employment', value: profile?.employment_type },
                ].filter(f => f.value).map(f => (
                  <div key={f.label} className="flex items-center gap-2.5 text-sm">
                    <f.icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span className="text-xs text-muted-foreground w-20 flex-shrink-0">{f.label}</span>
                    <span className="text-sm">{f.value}</span>
                  </div>
                ))}
                {/* Promote / Change Role button — admin only */}
                {canEdit && (
                <div className="pt-1">
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => {
                    if (onChangeRole) onChangeRole(person, profile);
                  }} data-testid="promote-role-btn">
                    <ArrowUpRight className="w-3 h-3 mr-1" />Promote / Change Role
                  </Button>
                </div>
                )}
                {/* Role History */}
                {profile?.role_history?.length > 0 && (
                  <div>
                    <p className="text-[11px] font-medium text-muted-foreground mb-1.5 flex items-center gap-1"><History className="w-3.5 h-3.5" />Role History</p>
                    <div className="space-y-1 pl-3 border-l-2 border-muted">
                      {[...profile.role_history].reverse().map((rh, ri) => (
                        <div key={ri} className="text-xs">
                          <span className="font-medium">{rh.role || 'Unknown'}</span>
                          {rh.department && <span className="ml-1 text-muted-foreground">({rh.department})</span>}
                          <div className="text-[10px] text-muted-foreground/60">
                            {rh.start_date && rh.end_date ? `${rh.start_date} to ${rh.end_date}` : rh.end_date ? `Until ${rh.end_date}` : ''}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {profile?.skills?.length > 0 && (
                  <div>
                    <p className="text-[11px] font-medium text-muted-foreground mb-1.5 flex items-center gap-1"><GraduationCap className="w-3.5 h-3.5" />Skills</p>
                    <div className="flex flex-wrap gap-1">{profile.skills.map(s => <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>)}</div>
                  </div>
                )}
                {profile?.bio && <div><p className="text-[11px] font-medium text-muted-foreground mb-1">Bio</p><p className="text-xs text-muted-foreground leading-relaxed">{profile.bio}</p></div>}

                {/* ---- Comprehensive Profile Sections ---- */}
                <ProfileSection title="Personal Information" icon={UserCheck}>
                  <ProfileField label="Staff Number" value={profile?.staff_number} />
                  <ProfileField label="First Name(s)" value={profile?.first_names} sub="As written in passport/ID" />
                  <ProfileField label="Family Name" value={profile?.family_name} sub="As written in passport/ID" />
                  <ProfileField label="Preferred Name" value={profile?.preferred_name} />
                  <ProfileField label="Personal Email" value={profile?.personal_email} />
                  <ProfileField label="Internal Email" value={profile?.internal_email || person?.email} />
                  <ProfileField label="Birthday" value={profile?.birthday} />
                  <ProfileField label="Home Address" value={profile?.home_address} />
                  <ProfileField label="ID Type" value={profile?.id_type} />
                  <ProfileField label="ID Number" value={profile?.id_number} />
                  <ProfileField label="T-Shirt Size" value={profile?.tshirt_size} />
                </ProfileSection>

                <ProfileSection title="Contact" icon={Phone}>
                  <ProfileField label="WhatsApp" value={profile?.phone_whatsapp} />
                  <ProfileField label="Telegram" value={profile?.phone_telegram} />
                  <ProfileField label="Line/Zalo/Telegram ID" value={profile?.messaging_id} />
                </ProfileSection>

                <ProfileSection title="Emergency Contact" icon={Shield}>
                  <ProfileField label="Full Name" value={profile?.emergency_contact_name} />
                  <ProfileField label="Relationship" value={profile?.emergency_contact_relationship} />
                  <ProfileField label="Phone Number" value={profile?.emergency_contact_phone} />
                </ProfileSection>

                <ProfileSection title="Banking Details" icon={Building2}>
                  <ProfileField label="Bank Name" value={profile?.bank_name} />
                  <ProfileField label="Account Name" value={profile?.account_name} />
                  <ProfileField label="Account Number" value={profile?.bank_account_number} />
                  <ProfileField label="Sort Code / Routing" value={profile?.sort_code} />
                  <ProfileField label="SWIFT / BIC" value={profile?.swift_bic} />
                  <ProfileField label="IBAN" value={profile?.iban} />
                  <ProfileField label="Bank Address" value={profile?.bank_address} />
                </ProfileSection>

                {!profile?.job_title && !profile?.department && !profile?.staff_number && <p className="text-xs text-muted-foreground text-center py-4">No profile info yet. Click Edit to add details.</p>}
              </div>
            )}
          </div>
        )}

        {detailTab === 'performance' && (
          <div className="p-4 space-y-3">
            <Button size="sm" className="w-full" onClick={() => setShowAddReview(true)} data-testid="add-review-btn"><Plus className="w-3.5 h-3.5 mr-1" />Add Review / 1:1 Note</Button>
            {reviews.length === 0 ? <p className="text-center text-xs text-muted-foreground py-6">No reviews yet</p> :
              reviews.map(r => (
                <Card key={r.review_id} className="overflow-hidden">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between mb-1">
                      <Badge variant="outline" className="text-[10px]">{r.type === 'one_on_one' ? '1:1 Note' : 'Review'}</Badge>
                      {r.rating && <div className="flex">{[1,2,3,4,5].map(s => <Star key={s} className={cn("w-3 h-3", s <= r.rating ? "text-amber-400 fill-amber-400" : "text-muted-foreground/20")} />)}</div>}
                    </div>
                    <h4 className="text-sm font-medium">{r.title}</h4>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-3">{r.content}</p>
                    {r.goals?.length > 0 && <div className="mt-2 space-y-1">{r.goals.map((g, i) => <div key={i} className="text-[11px] flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-500" />{g}</div>)}</div>}
                    <p className="text-[10px] text-muted-foreground mt-2">{new Date(r.created_at).toLocaleDateString()}</p>
                  </CardContent>
                </Card>
              ))}
          </div>
        )}

        {detailTab === 'leave' && (
          <div className="p-4 space-y-3">
            <Button size="sm" className="w-full" onClick={() => setShowAddLeave(true)} data-testid="add-leave-btn"><Plus className="w-3.5 h-3.5 mr-1" />Record Leave</Button>
            {leave.length === 0 ? <p className="text-center text-xs text-muted-foreground py-6">No leave records</p> :
              leave.map(l => (
                <div key={l.leave_id} className="border rounded-lg p-3 space-y-1" data-testid={`leave-${l.leave_id}`}>
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-[10px] capitalize">{l.type}</Badge>
                    <Badge className={cn("text-[10px]", LEAVE_STATUSES[l.status])}>{l.status}</Badge>
                  </div>
                  <p className="text-xs font-mono text-muted-foreground">{l.start_date}{l.end_date && ` → ${l.end_date}`}</p>
                  {l.notes && <p className="text-xs text-muted-foreground">{l.notes}</p>}
                  {l.status === 'pending' && (
                    <div className="flex gap-1 pt-1">
                      <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => onUpdateLeaveStatus(l.leave_id, 'approved')}>Approve</Button>
                      <Button size="sm" variant="ghost" className="h-6 text-[10px] text-red-500" onClick={() => onUpdateLeaveStatus(l.leave_id, 'rejected')}>Reject</Button>
                    </div>
                  )}
                </div>
              ))}
          </div>
        )}
      </ScrollArea>

      {/* Add Review Dialog */}
      <Dialog open={showAddReview} onOpenChange={setShowAddReview}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-sm">Add Review / 1:1 Note</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Select value={reviewForm.type} onValueChange={v => setReviewForm(f => ({...f, type: v}))}>
              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="review">Performance Review</SelectItem><SelectItem value="one_on_one">1:1 Note</SelectItem></SelectContent>
            </Select>
            <Input placeholder="Title" value={reviewForm.title} onChange={e => setReviewForm(f => ({...f, title: e.target.value}))} />
            <Textarea placeholder="Notes, feedback, discussion points..." value={reviewForm.content} onChange={e => setReviewForm(f => ({...f, content: e.target.value}))} rows={4} />
            {reviewForm.type === 'review' && (
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Rating</label>
                <div className="flex gap-1">{[1,2,3,4,5].map(s => (
                  <button key={s} onClick={() => setReviewForm(f => ({...f, rating: s}))}
                    className={cn("w-8 h-8 rounded-lg border flex items-center justify-center transition-colors",
                      s <= (reviewForm.rating || 0) ? "bg-amber-50 border-amber-300" : "hover:bg-muted"
                    )}><Star className={cn("w-4 h-4", s <= (reviewForm.rating || 0) ? "text-amber-400 fill-amber-400" : "text-muted-foreground/30")} /></button>
                ))}</div></div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddReview(false)}>Cancel</Button>
            <Button onClick={() => { onAddReview(person.id || person.user_id, reviewForm); setShowAddReview(false); setReviewForm({ type: 'review', title: '', content: '', rating: null }); }}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Leave Dialog */}
      <Dialog open={showAddLeave} onOpenChange={setShowAddLeave}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-sm">Record Leave</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Select value={leaveForm.type} onValueChange={v => setLeaveForm(f => ({...f, type: v}))}>
              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent>{LEAVE_TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-2">
              <Input type="date" value={leaveForm.start_date} onChange={e => setLeaveForm(f => ({...f, start_date: e.target.value}))} className="h-8 font-mono text-sm" />
              <Input type="date" value={leaveForm.end_date} onChange={e => setLeaveForm(f => ({...f, end_date: e.target.value}))} className="h-8 font-mono text-sm" />
            </div>
            <Input placeholder="Notes (optional)" value={leaveForm.notes} onChange={e => setLeaveForm(f => ({...f, notes: e.target.value}))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddLeave(false)}>Cancel</Button>
            <Button onClick={() => { onAddLeave(person.id || person.user_id, leaveForm); setShowAddLeave(false); setLeaveForm({ type: 'annual', start_date: '', end_date: '', notes: '' }); }}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== MAIN PAGE ====================

const OrganisationPage = () => {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const { orgId, handle409 } = useOrg();
  const [tab, setTab] = useState('people');

  // Org state
  const [organization, setOrganization] = useState(null);
  const [members, setMembers] = useState([]);
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [creatingOrg, setCreatingOrg] = useState(false);

  // Workspace state
  const [wsStatus, setWsStatus] = useState(null);
  const [wsUsers, setWsUsers] = useState([]);
  const [syncing, setSyncing] = useState(false);

  // Delegation state
  const [delegations, setDelegations] = useState({ as_supporter: [], as_principal: [] });
  const [showCreateDelegation, setShowCreateDelegation] = useState(false);
  const [delegationEmail, setDelegationEmail] = useState('');
  const [delegationProfileIds, setDelegationProfileIds] = useState([]);

  // Add user state
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserRole, setNewUserRole] = useState('user');
  const [sendInvite, setSendInvite] = useState(true);

  // Manage Org state
  const [orgForm, setOrgForm] = useState({});
  const [orgFiles, setOrgFiles] = useState([]);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // People/Talent state
  const [talentProfiles, setTalentProfiles] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [personProfile, setPersonProfile] = useState(null);
  const [personReviews, setPersonReviews] = useState([]);
  const [personLeave, setPersonLeave] = useState([]);

  // Change Role state
  const [changeRolePerson, setChangeRolePerson] = useState(null);
  const [changeRoleForm, setChangeRoleForm] = useState({ new_role: '', new_department: '', effective_date: new Date().toISOString().slice(0, 10) });

  // Import/Export state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importLoading, setImportLoading] = useState(false);


  // Org chart
  const [chartData, setChartData] = useState([]);

  // Skills matrix
  const [skillsMatrix, setSkillsMatrix] = useState({ skills: [], people: [] });

  // Audit
  const [auditLogs, setAuditLogs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  // Departments
  const [departments, setDepartments] = useState([]);
  const [deptForm, setDeptForm] = useState({ name: '', description: '', head_user_id: '' });
  const [editingDept, setEditingDept] = useState(null);
  const [showDeptForm, setShowDeptForm] = useState(false);

  // Permissions modal
  const [permissionsUser, setPermissionsUser] = useState(null);
  const [permForm, setPermForm] = useState({ modules: [], finance_permissions: [], is_orka_user: false });

  // Finance workspaces for permissions
  const [financeWorkspaces, setFinanceWorkspaces] = useState([]);

  // Merge modal
  const [showMerge, setShowMerge] = useState(false);
  const [mergeManual, setMergeManual] = useState('');
  const [mergeWorkspace, setMergeWorkspace] = useState('');

  // Access & Permissions state
  const [accessOverview, setAccessOverview] = useState([]);
  const [accessProfiles, setAccessProfiles] = useState([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [editingAccess, setEditingAccess] = useState(null);
  const [myAccessProfile, setMyAccessProfile] = useState(null);
  const [myUserId, setMyUserId] = useState(null);

  // Grant traceability modal
  const [traceModal, setTraceModal] = useState(null); // membership_id or null
  const [traceData, setTraceData] = useState(null);
  const [traceLoading, setTraceLoading] = useState(false);

  // Create profile modal
  const [createProfileOpen, setCreateProfileOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', description: '', permission_keys: [] });
  const [creatingProfile, setCreatingProfile] = useState(false);

  // Delete profile modal
  const [deleteProfileTarget, setDeleteProfileTarget] = useState(null); // profile object
  const [deleteProfileMembers, setDeleteProfileMembers] = useState([]);
  const [deleteProfileLoading, setDeleteProfileLoading] = useState(false);

  // Edit profile modal
  const [editProfileTarget, setEditProfileTarget] = useState(null); // profile object being edited
  const [editProfileForm, setEditProfileForm] = useState({ name: '', description: '', permission_keys: [] });
  const [editProfileOriginalKeys, setEditProfileOriginalKeys] = useState(new Set());
  const [editProfileMembers, setEditProfileMembers] = useState([]);
  const [editProfileLoading, setEditProfileLoading] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  // Derived: is current user an admin (has edit/manage permissions)?
  const isAdmin = myAccessProfile?.profile_key === 'admin';
  const canEditMembers = myAccessProfile?.permissions?.includes('org.members.edit') || false;
  const canManagePermissions = myAccessProfile?.permissions?.includes('org.members.permissions.update') || false;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [orgRes, membersRes, wsStatusRes, wsUsersRes, delegRes, myProfileRes] = await Promise.all([
        fetch(`${API}/org/current${orgId ? `?org_id=${orgId}` : ''}`, { credentials: 'include' }),
        fetch(`${API}/org/members${orgId ? `?org_id=${orgId}` : ''}`, { credentials: 'include' }),
        fetch(`${API}/org/workspace/status${orgId ? `?org_id=${orgId}` : ''}`, { credentials: 'include' }),
        fetch(`${API}/org/workspace/users${orgId ? `?org_id=${orgId}` : ''}`, { credentials: 'include' }),
        fetch(`${API}/org/delegations${orgId ? `?org_id=${orgId}` : ''}`, { credentials: 'include' }),
        fetch(`${API}/org/access/my-profile${orgId ? `?org_id=${orgId}` : ''}`, { credentials: 'include' }),
      ]);
      if (orgRes.status === 409) { handle409(); }
      else if (orgRes.ok) { const d = await orgRes.json(); setOrganization(d?.org_id ? d : null); if (d?.org_id) setOrgForm(d); }
      if (membersRes.ok) setMembers(await membersRes.json());
      if (wsStatusRes.ok) setWsStatus(await wsStatusRes.json());
      if (wsUsersRes.ok) setWsUsers(await wsUsersRes.json());
      if (delegRes.ok) setDelegations(await delegRes.json());
      if (myProfileRes.ok) {
        const mp = await myProfileRes.json();
        setMyAccessProfile(mp);
      }
      // Also get current user ID
      try {
        const meRes = await fetch(`${API}/auth/me`, { credentials: 'include' });
        if (meRes.ok) { const me = await meRes.json(); setMyUserId(me.user_id); }
      } catch {}
    } catch (err) { console.error('Failed to load org data:', err); }
    finally { setLoading(false); }
  }, [orgId]);

  const fetchTalent = useCallback(async () => {
    try {
      const res = await fetch(`${API}/org/talent/all`, { credentials: 'include' });
      if (res.ok) setTalentProfiles(await res.json());
    } catch {}
  }, []);

  const fetchChart = useCallback(async () => {
    try { const res = await fetch(`${API}/org/chart`, { credentials: 'include' }); if (res.ok) setChartData(await res.json()); } catch {}
  }, []);

  const fetchSkillsMatrix = useCallback(async () => {
    try { const res = await fetch(`${API}/org/skills-matrix`, { credentials: 'include' }); if (res.ok) setSkillsMatrix(await res.json()); } catch {}
  }, []);

  const fetchOrgFiles = useCallback(async () => {
    try { const res = await fetch(`${API}/org/files`, { credentials: 'include' }); if (res.ok) setOrgFiles(await res.json()); } catch {}
  }, []);

  const fetchDepartments = useCallback(async () => {
    try { const r = await fetch(`${API}/org/departments`, { credentials: 'include' }); if (r.ok) setDepartments(await r.json()); } catch {}
  }, []);

  const fetchFinanceWorkspaces = useCallback(async () => {
    try { const r = await fetch(`${API}/finance/workspaces${orgId ? `?org_id=${orgId}` : ''}`, { credentials: 'include' }); if (r.ok) setFinanceWorkspaces(await r.json()); } catch {}
  }, []);

  const fetchAudit = useCallback(async () => {
    try { const res = await fetch(`${API}/org/workspace/audit?limit=100`, { credentials: 'include' }); if (res.ok) setAuditLogs(await res.json()); } catch {}
  }, []);

  const fetchAccessOverview = useCallback(async () => {
    setAccessLoading(true);
    try {
      const [overviewRes, profilesRes, myProfileRes] = await Promise.all([
        fetch(`${API}/org/access/overview`, { credentials: 'include' }),
        fetch(`${API}/org/access/profiles`, { credentials: 'include' }),
        fetch(`${API}/org/access/my-profile`, { credentials: 'include' }),
      ]);
      if (overviewRes.ok) setAccessOverview(await overviewRes.json());
      if (profilesRes.ok) setAccessProfiles(await profilesRes.json());
      if (myProfileRes.ok) setMyAccessProfile(await myProfileRes.json());
    } catch (err) { console.error('Failed to load access data:', err); }
    finally { setAccessLoading(false); }
  }, []);

  const handleAssignProfile = async (membershipId, profileKey) => {
    try {
      const res = await fetch(`${API}/org/access/grant/${membershipId}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profile_key: profileKey }),
      });
      if (res.ok) {
        toast.success('Access profile updated');
        setEditingAccess(null);
        fetchAccessOverview();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update profile');
      }
    } catch { toast.error('Network error'); }
  };

  const handleRevokeGrant = async (membershipId) => {
    try {
      const res = await fetch(`${API}/org/access/grant/${membershipId}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (res.ok) {
        toast.success('Explicit grant revoked — falling back to legacy mapping');
        fetchAccessOverview();
      }
    } catch { toast.error('Network error'); }
  };

  // Grant traceability
  const openTraceModal = async (membershipId) => {
    setTraceModal(membershipId);
    setTraceLoading(true);
    setTraceData(null);
    try {
      const res = await fetch(`${API}/org/access/grant/${membershipId}/details`, { credentials: 'include' });
      if (res.ok) setTraceData(await res.json());
      else toast.error('Failed to load grant details');
    } catch { toast.error('Network error'); }
    finally { setTraceLoading(false); }
  };

  // Create custom profile
  const handleCreateProfile = async () => {
    if (!profileForm.name.trim()) { toast.error('Profile name is required'); return; }
    setCreatingProfile(true);
    try {
      const res = await fetch(`${API}/org/access/profiles`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: profileForm.name, description: profileForm.description, permission_keys: profileForm.permission_keys }),
      });
      if (res.ok) {
        toast.success('Permission profile created');
        setCreateProfileOpen(false);
        setProfileForm({ name: '', description: '', permission_keys: [] });
        fetchAccessOverview();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create profile');
      }
    } catch { toast.error('Network error'); }
    finally { setCreatingProfile(false); }
  };

  // Delete profile flow
  const openDeleteProfile = async (profile) => {
    setDeleteProfileTarget(profile);
    setDeleteProfileLoading(true);
    setDeleteProfileMembers([]);
    try {
      const res = await fetch(`${API}/org/access/profiles/${profile.key || profile.access_profile_id}/members`, { credentials: 'include' });
      if (res.ok) setDeleteProfileMembers(await res.json());
    } catch {}
    finally { setDeleteProfileLoading(false); }
  };

  const handleDeleteProfile = async () => {
    if (!deleteProfileTarget) return;
    setDeleteProfileLoading(true);
    try {
      const res = await fetch(`${API}/org/access/profiles/${deleteProfileTarget.key || deleteProfileTarget.access_profile_id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Profile deleted${data.downgraded_count > 0 ? `. ${data.downgraded_count} member(s) downgraded to User.` : ''}`);
        setDeleteProfileTarget(null);
        fetchAccessOverview();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete profile');
      }
    } catch { toast.error('Network error'); }
    finally { setDeleteProfileLoading(false); }
  };

  // Permission section toggle helper for create-profile form
  const togglePermission = (key) => {
    setProfileForm(prev => {
      const keys = new Set(prev.permission_keys);
      if (keys.has(key)) keys.delete(key); else keys.add(key);
      return { ...prev, permission_keys: [...keys] };
    });
  };
  const toggleSection = (sectionKeys, allSelected) => {
    setProfileForm(prev => {
      const keys = new Set(prev.permission_keys);
      sectionKeys.forEach(k => allSelected ? keys.delete(k) : keys.add(k));
      return { ...prev, permission_keys: [...keys] };
    });
  };

  // Edit profile flow
  const openEditProfile = async (profile) => {
    setEditProfileTarget(profile);
    const currentKeys = profile.permissions || [];
    setEditProfileForm({ name: profile.label || '', description: profile.description || '', permission_keys: [...currentKeys] });
    setEditProfileOriginalKeys(new Set(currentKeys));
    setEditProfileLoading(true);
    setEditProfileMembers([]);
    try {
      const res = await fetch(`${API}/org/access/profiles/${profile.key || profile.access_profile_id}/members`, { credentials: 'include' });
      if (res.ok) setEditProfileMembers(await res.json());
    } catch {}
    finally { setEditProfileLoading(false); }
  };

  const handleSaveProfile = async () => {
    if (!editProfileTarget) return;
    setSavingProfile(true);
    try {
      const res = await fetch(`${API}/org/access/profiles/${editProfileTarget.key || editProfileTarget.access_profile_id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editProfileForm.name, description: editProfileForm.description, permission_keys: editProfileForm.permission_keys }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Profile updated (v${data.version})`);
        setEditProfileTarget(null);
        fetchAccessOverview();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update profile');
      }
    } catch { toast.error('Network error'); }
    finally { setSavingProfile(false); }
  };

  // Toggle helpers for edit form
  const toggleEditPermission = (key) => {
    setEditProfileForm(prev => {
      const keys = new Set(prev.permission_keys);
      if (keys.has(key)) keys.delete(key); else keys.add(key);
      return { ...prev, permission_keys: [...keys] };
    });
  };
  const toggleEditSection = (sectionKeys, allSelected) => {
    setEditProfileForm(prev => {
      const keys = new Set(prev.permission_keys);
      sectionKeys.forEach(k => allSelected ? keys.delete(k) : keys.add(k));
      return { ...prev, permission_keys: [...keys] };
    });
  };

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { if (tab === 'people') { fetchTalent(); fetchFinanceWorkspaces(); } }, [tab, fetchTalent, fetchFinanceWorkspaces]);
  useEffect(() => { if (tab === 'departments' || tab === 'people') fetchDepartments(); }, [tab, fetchDepartments]);
  useEffect(() => { if (tab === 'chart') fetchChart(); }, [tab, fetchChart]);
  useEffect(() => { if (tab === 'skills') fetchSkillsMatrix(); }, [tab, fetchSkillsMatrix]);
  useEffect(() => { if (tab === 'manage') fetchOrgFiles(); }, [tab, fetchOrgFiles]);
  useEffect(() => { if (tab === 'audit') fetchAudit(); }, [tab, fetchAudit]);
  useEffect(() => { if (tab === 'access') fetchAccessOverview(); }, [tab, fetchAccessOverview]);

  // Eagerly load profiles so the Add User dialog always has options
  useEffect(() => {
    if (accessProfiles.length === 0 && organization) {
      fetch(`${API}/org/access/profiles`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : [])
        .then(setAccessProfiles)
        .catch(() => {});
    }
  }, [organization]);

  // Load person detail
  useEffect(() => {
    if (selectedPerson) {
      const uid = selectedPerson.id || selectedPerson.user_id;
      Promise.all([
        fetch(`${API}/org/members/${uid}/profile`, { credentials: 'include' }).then(r => r.ok ? r.json() : {}),
        fetch(`${API}/org/members/${uid}/reviews`, { credentials: 'include' }).then(r => r.ok ? r.json() : []),
        fetch(`${API}/org/members/${uid}/leave`, { credentials: 'include' }).then(r => r.ok ? r.json() : []),
      ]).then(([profile, reviews, leave]) => { setPersonProfile(profile); setPersonReviews(reviews); setPersonLeave(leave); });
    }
  }, [selectedPerson]);

  const createOrganization = async () => {
    if (!orgName.trim()) { toast.error('Organization name required'); return; }
    setCreatingOrg(true);
    try {
      const res = await fetch(`${API}/org/create`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ name: orgName.trim() }) });
      if (res.ok) {
        toast.success('Organization created');
        setShowCreateOrg(false);
        setOrgName('');
        fetchAll();
      } else {
        let detail = '';
        try { const err = await res.json(); detail = err.detail || err.message || JSON.stringify(err); }
        catch { try { detail = await res.text(); } catch { detail = ''; } }
        const msg = `Create org failed (${res.status})${detail ? `: ${detail}` : ''}`;
        toast.error(msg);
        // eslint-disable-next-line no-console
        console.error('Create organisation failed', { status: res.status, detail });
      }
    } catch (e) {
      toast.error(`Network error: ${e?.message || 'unknown'}`);
      // eslint-disable-next-line no-console
      console.error('Create organisation network error', e);
    } finally {
      setCreatingOrg(false);
    }
  };

  const saveOrgInfo = async () => {
    try {
      const res = await fetch(`${API}/org/update`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ name: orgForm.name, description: orgForm.description, industry: orgForm.industry, website: orgForm.website, address: orgForm.address, phone: orgForm.phone, size: orgForm.size, founded: orgForm.founded, context_notes: orgForm.context_notes }) });
      if (res.ok) { const d = await res.json(); setOrganization(d); toast.success('Saved'); }
    } catch { toast.error('Failed'); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch(`${API}/org/logo`, { method: 'POST', credentials: 'include', body: fd });
      if (res.ok) { const d = await res.json(); setOrgForm(f => ({...f, logo_url: d.logo_url})); setOrganization(o => ({...o, logo_url: d.logo_url})); toast.success('Logo updated'); }
    } catch { toast.error('Upload failed'); } finally { setUploadingLogo(false); }
  };

  const uploadOrgFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch(`${API}/org/files`, { method: 'POST', credentials: 'include', body: fd });
      if (res.ok) { fetchOrgFiles(); toast.success('File uploaded'); }
    } catch { toast.error('Upload failed'); }
  };

  const deleteOrgFile = async (fileId) => {
    await fetch(`${API}/org/files/${fileId}`, { method: 'DELETE', credentials: 'include' });
    fetchOrgFiles(); toast.success('Deleted');
  };

  const saveProfile = async (userId, data) => {
    try {
      const res = await fetch(`${API}/org/members/${userId}/profile`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(data) });
      if (res.ok) { setPersonProfile(await res.json()); fetchTalent(); fetchChart(); toast.success('Profile saved'); }
    } catch { toast.error('Failed'); }
  };

  const handleChangeRole = async () => {
    if (!changeRolePerson || !changeRoleForm.new_role.trim()) return;
    try {
      const res = await fetch(`${API}/org/members/${changeRolePerson.id}/change-role`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(changeRoleForm),
      });
      if (res.ok) {
        toast.success(`Role changed to ${changeRoleForm.new_role}`);
        setChangeRolePerson(null);
        setChangeRoleForm({ new_role: '', new_department: '', effective_date: new Date().toISOString().slice(0, 10) });
        fetchTalent();
        if (selectedPerson?.id === changeRolePerson.id) {
          const pr = await fetch(`${API}/org/members/${changeRolePerson.id}/profile`, { credentials: 'include' });
          if (pr.ok) setPersonProfile(await pr.json());
        }
      }
    } catch { toast.error('Failed to change role'); }
  };

  const handleExportPeople = async () => {
    try {
      const r = await fetch(`${API}/org/people/export`, { credentials: 'include' });
      if (!r.ok) throw new Error();
      const { fields, data } = await r.json();
      const header = fields.join(',');
      const rows = data.map(row => fields.map(f => {
        const v = String(row[f] || '');
        return v.includes(',') || v.includes('"') || v.includes('\n') ? `"${v.replace(/"/g, '""')}"` : v;
      }).join(','));
      const csv = [header, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'people_export.csv'; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${data.length} people`);
    } catch { toast.error('Export failed'); }
  };

  const handleDownloadPeopleTemplate = async () => {
    try {
      const r = await fetch(`${API}/org/people/import-template`, { credentials: 'include' });
      if (!r.ok) throw new Error();
      const { fields } = await r.json();
      const csv = fields.join(',') + '\n';
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'people_import_template.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download template'); }
  };

  const handlePeopleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    Papa.parse(file, {
      header: true, skipEmptyLines: true,
      complete: (result) => {
        setImportPreview(result.data);
        setShowImportDialog(true);
      },
      error: () => toast.error('Failed to parse CSV'),
    });
    e.target.value = '';
  };

  const handleConfirmImport = async () => {
    if (!importPreview?.length) return;
    setImportLoading(true);
    try {
      const r = await fetch(`${API}/org/people/import`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ records: importPreview }),
      });
      if (r.ok) {
        const res = await r.json();
        if (res.updated === 0 && res.created === 0 && res.skipped > 0) {
          toast.error(`Import: all ${res.skipped} records skipped — check that your CSV has an "email" column matching existing member emails`);
        } else {
          toast.success(`Import complete: ${res.updated} updated, ${res.created} created${res.skipped > 0 ? `, ${res.skipped} skipped` : ''}`);
        }
        if (res.errors?.length) {
          res.errors.forEach(e => toast.error(e, { duration: 8000 }));
        }
        setShowImportDialog(false);
        setImportPreview(null);
        fetchUsers(); fetchTalent();
      } else {
        const err = await r.json().catch(() => null);
        toast.error(err?.detail || 'Import failed');
      }
    } catch { toast.error('Import failed — network error'); }
    setImportLoading(false);
  };


  const addReview = async (userId, data) => {
    try {
      await fetch(`${API}/org/members/${userId}/reviews`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(data) });
      const res = await fetch(`${API}/org/members/${userId}/reviews`, { credentials: 'include' });
      if (res.ok) setPersonReviews(await res.json());
      toast.success('Saved');
    } catch { toast.error('Failed'); }
  };

  const addLeave = async (userId, data) => {
    try {
      await fetch(`${API}/org/members/${userId}/leave`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(data) });
      const res = await fetch(`${API}/org/members/${userId}/leave`, { credentials: 'include' });
      if (res.ok) setPersonLeave(await res.json());
      toast.success('Saved');
    } catch { toast.error('Failed'); }
  };

  const updateLeaveStatus = async (leaveId, status) => {
    await fetch(`${API}/org/leave/${leaveId}/status`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ status }) });
    const uid = selectedPerson?.id || selectedPerson?.user_id;
    if (uid) { const res = await fetch(`${API}/org/members/${uid}/leave`, { credentials: 'include' }); if (res.ok) setPersonLeave(await res.json()); }
    toast.success(`Leave ${status}`);
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API}/org/workspace/sync`, { method: 'POST', credentials: 'include' });
      if (res.ok) { const data = await res.json(); toast.success('Sync complete', { description: `${data.stats.users_synced} synced, ${data.stats.users_created} created` }); fetchAll(); }
    } catch { toast.error('Sync failed'); } finally { setSyncing(false); }
  };

  const addManualUser = async () => {
    if (!newUserEmail.trim()) { toast.error('Email required'); return; }
    try {
      const res = await fetch(`${API}/org/members`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ email: newUserEmail.trim(), name: newUserName.trim() || undefined, role: 'Member', access_profile_id: newUserRole, send_invite: sendInvite }) });
      if (res.ok) {
        const data = await res.json();
        if (data.invite_link) {
          const fullLink = `${window.location.origin}${data.invite_link}`;
          navigator.clipboard.writeText(fullLink).catch(() => {});
          toast.success('User added & invite link copied', { description: `Invite link for ${newUserEmail.trim()} copied to clipboard`, duration: 8000 });
        } else {
          toast.success('User added');
        }
        setShowAddUser(false); setNewUserEmail(''); setNewUserName(''); setNewUserRole('user'); setSendInvite(true); fetchAll();
      }
      else { try { const err = await res.json(); toast.error(err.detail || 'Failed'); } catch { toast.error('Failed'); } }
    } catch { toast.error('Failed'); }
  };

  const createDelegation = async () => {
    if (!delegationEmail.trim() || !delegationProfileIds.length) { toast.error('Email and access profile required'); return; }
    try {
      const res = await fetch(`${API}/org/delegations`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ from_user_id: user.user_id, to_user_id: delegationEmail.trim(), access_profile_ids: delegationProfileIds, delegation_scope_type: 'all_tasks' }) });
      if (res.ok) { toast.success('Created'); setShowCreateDelegation(false); setDelegationEmail(''); setDelegationProfileIds([]); fetchAll(); }
      else { const err = await res.json().catch(() => ({})); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Failed'); }
  };

  const revokeDelegation = async (id) => {
    await fetch(`${API}/org/delegations/${id}`, { method: 'DELETE', credentials: 'include' });
    toast.success('Revoked'); fetchAll();
  };

  const deleteMember = async (userId) => {
    if (!confirm('Remove this person from your organisation?')) return;
    try {
      const r = await fetch(`${API}/org/members/${userId}`, { method: 'DELETE', credentials: 'include' });
      if (r.ok) { toast.success('Removed'); fetchAll(); }
      else {
        try { const e = await r.json(); toast.error(e.detail || 'Cannot delete'); }
        catch { toast.error('Cannot delete this user'); }
      }
    } catch { toast.error('Failed to delete user'); }
  };

  // Epic — "forget user" reset. memberToReset is shown via a confirm dialog.
  const [memberToReset, setMemberToReset] = useState(null);
  const [resettingMember, setResettingMember] = useState(false);
  const [resetWithInvite, setResetWithInvite] = useState(true);

  const confirmResetMember = async () => {
    if (!memberToReset) return;
    setResettingMember(true);
    try {
      const qs = orgId ? `?org_id=${encodeURIComponent(orgId)}` : '';
      const r = await fetch(`${API}/org/members/${encodeURIComponent(memberToReset.id)}/reset${qs}`, {
        method: 'POST', credentials: 'include',
      });
      if (!r.ok) {
        let detail = '';
        try { const e = await r.json(); detail = e.detail || ''; } catch { /* ignore */ }
        toast.error(`Reset failed (${r.status})${detail ? `: ${detail}` : ''}`);
        return;
      }
      const data = await r.json();
      const summary = data.removed || {};
      const bits = [];
      if (summary.user_deleted) bits.push('user wiped');
      if (summary.person_deleted) bits.push('person wiped');
      if (summary.access_grants) bits.push(`${summary.access_grants} grant(s)`);
      if (summary.invitations) bits.push(`${summary.invitations} prior invite(s)`);
      toast.success(`Account reset for ${memberToReset.email}${bits.length ? ` — ${bits.join(', ')}` : ''}`);

      const emailToInvite = memberToReset.email;
      const nameToInvite = memberToReset.name || emailToInvite;
      const sendInviteAfter = resetWithInvite;
      setMemberToReset(null);

      if (sendInviteAfter && emailToInvite) {
        // Re-add the person + send a fresh invite by hitting the existing add-member flow.
        try {
          const r2 = await fetch(`${API}/org/members${orgId ? `?org_id=${encodeURIComponent(orgId)}` : ''}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              email: emailToInvite,
              name: nameToInvite,
              role: 'Member',
              access_profile_id: 'limited',
              send_invite: true,
            }),
          });
          if (r2.ok) {
            toast.success(`Fresh invite sent to ${emailToInvite}`);
          } else {
            try { const e = await r2.json(); toast.error(e.detail || 'Could not send fresh invite'); }
            catch { toast.error('Could not send fresh invite'); }
          }
        } catch (e) {
          toast.error(`Could not send fresh invite: ${e?.message || 'unknown'}`);
        }
      }

      fetchAll();
    } catch (e) {
      toast.error(`Reset failed: ${e?.message || 'unknown'}`);
    } finally {
      setResettingMember(false);
    }
  };

  const purgeOrphaned = async () => {
    if (!confirm('Remove all orphaned/unknown entries from the people table?')) return;
    try {
      const r = await fetch(`${API}/org/members/purge-orphaned`, { method: 'POST', credentials: 'include' });
      if (r.ok) { const d = await r.json(); toast.success(`Cleaned up ${d.purged} orphaned entries`); fetchAll(); }
      else { toast.error('Failed to purge'); }
    } catch { toast.error('Failed to purge'); }
  };

  const sendInviteEmail = async (userId, email) => {
    try {
      const qs = orgId ? `?org_id=${encodeURIComponent(orgId)}` : '';
      const r = await fetch(`${API}/org/members/${encodeURIComponent(userId)}/invite${qs}`, { method: 'POST', credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        const fullLink = `${window.location.origin}${data.invite_link}`;
        toast.success(`Invite created for ${email}`, {
          description: 'Link copied to clipboard',
          duration: 8000,
        });
        navigator.clipboard.writeText(fullLink).catch(() => {});
      }
      else { try { const e = await r.json(); toast.error(e.detail || 'Failed to send invite'); } catch { toast.error('Failed to send invite'); } }
    } catch { toast.error('Failed to send invite'); }
  };

  const toggleOrkaUser = async (userId, current) => {
    const r = await fetch(`${API}/org/members/${userId}/mark-orka`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_orka_user: !current }),
    });
    if (r.ok) { toast.success(current ? 'Removed Orka access' : 'Granted Orka access'); fetchAll(); }
  };

  const openPermissions = (user) => {
    setPermissionsUser(user);
    setPermForm({
      modules: user.modules || [],
      finance_permissions: user.finance_permissions || [],
      is_orka_user: user.is_orka_user || false,
    });
    if (!financeWorkspaces.length) fetchFinanceWorkspaces();
  };

  const savePermissions = async () => {
    if (!permissionsUser) return;
    const r = await fetch(`${API}/org/members/${permissionsUser.id}/permissions`, {
      method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(permForm),
    });
    if (r.ok) { toast.success('Permissions updated'); setPermissionsUser(null); fetchAll(); }
  };

  const doMerge = async () => {
    if (!mergeManual || !mergeWorkspace) { toast.error('Select both users'); return; }
    const r = await fetch(`${API}/org/members/merge`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ manual_user_id: mergeManual, workspace_user_id: mergeWorkspace }),
    });
    if (r.ok) { toast.success('Users merged'); setShowMerge(false); setMergeManual(''); setMergeWorkspace(''); fetchAll(); }
    else { const e = await r.json(); toast.error(e.detail || 'Merge failed'); }
  };

  const saveDept = async () => {
    const url = editingDept
      ? `${API}/org/departments/${editingDept.department_id}`
      : `${API}/org/departments`;
    const r = await fetch(url, {
      method: editingDept ? 'PUT' : 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(deptForm),
    });
    if (r.ok) { toast.success(editingDept ? 'Updated' : 'Created'); setShowDeptForm(false); setEditingDept(null); setDeptForm({ name: '', description: '', head_user_id: '' }); fetchDepartments(); }
  };

  const deleteDept = async (id) => {
    if (!confirm('Delete this department?')) return;
    await fetch(`${API}/org/departments/${id}`, { method: 'DELETE', credentials: 'include' });
    toast.success('Deleted'); fetchDepartments();
  };

  // Backend already returns merged members + workspace users — use directly.
  // ID priority: membership_id → person_id → email → user_id. Email comes BEFORE
  // user_id because workspace-synced people without an Orka identity store the
  // workspace UID in user_id, and that value is not resolvable by the backend.
  // Email is always resolvable via the invite/reset endpoints.
  const allUsers = useMemo(() => {
    return members.map(m => ({
      id: m.membership_id || m.person_id || (m.has_authenticated ? m.user_id : null) || m.email || m.user_id,
      person_id: m.person_id,
      user_id: m.user_id,
      membership_id: m.membership_id,
      name: m.name || 'Unknown',
      email: m.email || '',
      picture: m.picture || m.user?.picture,
      role: m.role || 'Member',
      source: m.source || 'manual',
      workspace_status: m.workspace_status || null,
      org_unit: m.org_unit || null,
      is_orka_user: m.is_orka_user !== false,
      has_user_account: m.has_user_account || false,
      has_authenticated: m.has_authenticated || false,
      account_status: m.account_status || 'person_only',
      invitation_status: m.invitation_status || null,
      membership_status: m.membership_status || null,
      modules: m.modules || [],
      finance_permissions: m.finance_permissions || [],
      can_delete: m.can_delete === true,
      is_self: m.is_self || false,
      is_owner: m.is_owner || false,
      department: m.user?.department || '',
      job_title: m.user?.job_title || '',
    }));
  }, [members]);

  const filteredUsers = useMemo(() => {
    if (!searchQuery) return allUsers;
    const q = searchQuery.toLowerCase();
    return allUsers.filter(u => (u.email || '').toLowerCase().includes(q) || (u.name || '').toLowerCase().includes(q));
  }, [allUsers, searchQuery]);

  // Enrich users with talent data (search by person_id or user_id)
  const enrichedUsers = useMemo(() => {
    return filteredUsers.map(u => {
      const profile = talentProfiles.find(p => (u.person_id && p.person_id === u.person_id) || (u.user_id && p.user_id === u.user_id));
      return { ...u, job_title: u.job_title || profile?.job_title, department: u.department || profile?.department, skills: profile?.skills, role_history: profile?.role_history || [] };
    });
  }, [filteredUsers, talentProfiles]);

  const tabs = [
    { id: 'people', label: 'People', icon: Users },
    { id: 'departments', label: 'Departments', icon: Building2 },
    { id: 'chart', label: 'Org Chart', icon: Network },
    { id: 'skills', label: 'Skills Matrix', icon: GraduationCap },
    { id: 'access', label: 'Access & Permissions', icon: ShieldCheck },
    { id: 'manage', label: 'Manage Org', icon: Settings },
    { id: 'delegations', label: 'Delegations', icon: Shield },
    { id: 'audit', label: 'Audit Log', icon: ScrollText },
  ];

  if (!loading && !organization) {
    return (
      <PageLayout theme={theme} setTheme={setTheme}>
        <div className="max-w-lg mx-auto p-6 mt-20">
          <Card><CardHeader className="text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-indigo-50 flex items-center justify-center mb-2"><Building2 className="w-7 h-7 text-indigo-600" /></div>
            <CardTitle>Create Your Organisation</CardTitle>
            <CardDescription>Set up an organisation to manage your team.</CardDescription>
          </CardHeader><CardContent className="space-y-3">
            <Input placeholder="Organisation name" value={orgName} onChange={(e) => setOrgName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && createOrganization()} data-testid="org-name-input" />
            <Button className="w-full" onClick={createOrganization} disabled={creatingOrg} data-testid="create-org-btn"><Plus className="w-4 h-4 mr-1.5" />{creatingOrg ? 'Creating...' : 'Create Organisation'}</Button>
          </CardContent></Card>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout theme={theme} setTheme={setTheme}>
      <div className="h-[calc(100vh-4rem)] flex flex-col overflow-hidden bg-background">
        {/* Header */}
        <div className="px-6 py-3 border-b bg-card flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            {organization?.logo_url ? <img src={organization.logo_url} alt="" className="w-8 h-8 rounded-lg object-cover border" /> :
              <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center"><Building2 className="w-4 h-4 text-indigo-600" /></div>}
            <div>
              <h1 className="text-lg font-bold text-foreground" data-testid="org-page-title">{organization?.name || 'Organisation'}</h1>
              <p className="text-[11px] text-muted-foreground">{allUsers.length} members{organization?.industry ? ` · ${organization.industry}` : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input type="file" accept=".csv" id="people-import-file" className="hidden" onChange={handlePeopleFileUpload} />
            {canEditMembers && <Button variant="outline" size="sm" onClick={handleDownloadPeopleTemplate} data-testid="people-template-btn"><FileText className="w-3.5 h-3.5 mr-1" />Template</Button>}
            {canEditMembers && <Button variant="outline" size="sm" onClick={() => document.getElementById('people-import-file').click()} data-testid="people-import-btn"><Upload className="w-3.5 h-3.5 mr-1" />Import</Button>}
            <Button variant="outline" size="sm" onClick={handleExportPeople} data-testid="people-export-btn"><FileText className="w-3.5 h-3.5 mr-1" />Export</Button>
            {canEditMembers && <Button variant="outline" size="sm" onClick={() => setShowAddUser(true)} data-testid="add-user-btn"><UserPlus className="w-4 h-4 mr-1.5" />Add User</Button>}
            {canEditMembers && wsStatus?.connected && <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing}><RefreshCw className={cn("w-4 h-4 mr-1", syncing && "animate-spin")} />{syncing ? 'Syncing...' : 'Sync'}</Button>}
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 py-1 border-b bg-card/50 flex items-center gap-1 shrink-0">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={cn("flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors",
                tab === t.id ? "border-indigo-600 text-indigo-600" : "border-transparent text-muted-foreground hover:text-foreground"
              )} data-testid={`tab-${t.id}`}>
              <t.icon className="w-3.5 h-3.5" />{t.label}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-8 h-7 w-44 text-xs" />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          <div className="flex-1 overflow-y-auto">
            {loading ? <div className="flex items-center justify-center py-20"><Loader2 className="w-5 h-5 animate-spin mr-2 text-muted-foreground" />Loading...</div> :

            /* People Tab */
            tab === 'people' ? (
              <div className="p-6 space-y-3">
                {/* Action bar */}
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => setShowMerge(true)} data-testid="merge-users-btn"><Merge className="w-3.5 h-3.5 mr-1" />Merge Users</Button>
                  {allUsers.some(u => u.is_orphaned) && <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={purgeOrphaned} data-testid="purge-orphaned-btn"><Trash2 className="w-3.5 h-3.5 mr-1" />Clean Up Unknown</Button>}
                </div>
                <div className="bg-card rounded-xl border overflow-auto max-h-[calc(100vh-250px)]">
                  <table className="w-full text-sm" data-testid="people-table">
                    <thead className="bg-muted/80 dark:bg-muted/50 border-b sticky top-0 z-10">
                      <tr>
                        <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">Person</th>
                        <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">Source</th>
                        <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">Job Title</th>
                        <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">Department</th>
                        <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">Account</th>
                        <th className="text-right px-4 py-2.5 font-medium text-muted-foreground text-xs w-36">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {enrichedUsers.length === 0 ? <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground text-xs">No users found</td></tr> :
                        enrichedUsers.map(u => (
                          <tr key={u.email || u.id} className="hover:bg-accent/50 group" data-testid={`person-row-${u.email}`}>
                            <td className="px-4 py-3 cursor-pointer" onClick={() => setSelectedPerson(u)}>
                              <div className="flex items-center gap-2.5">
                                {u.picture ? <img src={u.picture} alt="" className="w-8 h-8 rounded-full" /> :
                                  <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-semibold text-indigo-600">{(u.name || '?')[0].toUpperCase()}</div>}
                                <div><p className="font-medium text-sm">{u.name}</p><p className="text-[11px] text-muted-foreground">{u.email}</p></div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              {u.source === 'workspace' && <Badge className="bg-blue-100 text-blue-700 text-[10px]">Workspace</Badge>}
                              {u.source === 'manual' && <Badge className="bg-amber-100 text-amber-700 text-[10px]">Manual</Badge>}
                              {u.source === 'both' && <><Badge className="bg-blue-100 text-blue-700 text-[10px] mr-0.5">Workspace</Badge><Badge className="bg-purple-100 text-purple-700 text-[10px]">Orka</Badge></>}
                              {u.source === 'orphaned' && <Badge className="bg-red-100 text-red-700 text-[10px]">Orphaned</Badge>}
                              {u.source === 'restored_rca' && <Badge className="bg-teal-100 text-teal-700 text-[10px]">Restored</Badge>}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1.5 group/role">
                                <div className="flex-1 min-w-0">
                                  {u.job_title ? (
                                    <span className="text-sm">{u.job_title}</span>
                                  ) : (
                                    <span className="text-muted-foreground/40">-</span>
                                  )}
                                  {u.role_history?.length > 0 && (
                                    <details className="mt-0.5">
                                      <summary className="text-[10px] text-muted-foreground cursor-pointer hover:text-foreground flex items-center gap-0.5">
                                        <History className="w-2.5 h-2.5 inline" /> {u.role_history.length} previous
                                      </summary>
                                      <div className="mt-1 space-y-0.5 pl-3 border-l-2 border-muted">
                                        {[...u.role_history].reverse().map((rh, ri) => (
                                          <div key={ri} className="text-[10px] text-muted-foreground">
                                            <span className="font-medium">{rh.role || 'Unknown'}</span>
                                            {rh.department && <span className="ml-1 text-muted-foreground/60">({rh.department})</span>}
                                            <span className="ml-1 text-muted-foreground/40">
                                              {rh.start_date && rh.end_date ? `${rh.start_date} - ${rh.end_date}` : rh.end_date ? `until ${rh.end_date}` : ''}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    </details>
                                  )}
                                </div>
                                <button className={cn("shrink-0 p-0.5 rounded opacity-0 group-hover/role:opacity-100 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 text-muted-foreground hover:text-indigo-600 transition-opacity", !canEditMembers && "hidden")} title="Promote / Change Role" onClick={e => { e.stopPropagation(); setChangeRolePerson(u); setChangeRoleForm({ new_role: '', new_department: u.department || '', effective_date: new Date().toISOString().slice(0, 10) }); }} data-testid={`change-role-${u.email}`}>
                                  <ArrowUpRight className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                            <td className="px-4 py-3">{u.department ? <Badge variant="outline" className="text-[10px]">{u.department}</Badge> : <span className="text-muted-foreground/40">-</span>}</td>
                            <td className="px-4 py-3" data-testid={`account-${u.email}`}>
                              <div className="flex items-center gap-1">
                                {u.account_status === 'authenticated' && <Badge className="bg-emerald-100 text-emerald-600 text-[10px]">Signed In</Badge>}
                                {u.account_status === 'invited' && <Badge className="bg-amber-100 text-amber-700 text-[10px]">Invited</Badge>}
                                {(u.account_status === 'person_only' || u.account_status === 'provisioned') && <Badge className="bg-slate-100 text-slate-600 text-[10px]">Not Invited</Badge>}
                                {u.is_self && <Badge className="bg-indigo-100 text-indigo-600 text-[10px]">You</Badge>}
                                {u.is_owner && <Badge className="bg-violet-100 text-violet-600 text-[10px]">Owner</Badge>}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex gap-0.5 justify-end opacity-0 group-hover:opacity-100">
                                {canEditMembers && <Button variant="ghost" size="icon" className="h-7 w-7" title="View / Edit Profile" onClick={e => { e.stopPropagation(); setSelectedPerson(u); }} data-testid={`edit-${u.email}`}><Pencil className="w-3 h-3" /></Button>}
                                {!canEditMembers && <Button variant="ghost" size="icon" className="h-7 w-7" title="View Profile" onClick={e => { e.stopPropagation(); setSelectedPerson(u); }} data-testid={`view-${u.email}`}><Eye className="w-3 h-3" /></Button>}
                                {canEditMembers && u.email && <Button variant="ghost" size="icon" className="h-7 w-7" title={u.has_authenticated ? 'Re-send Invite' : 'Send Invite'} onClick={e => { e.stopPropagation(); sendInviteEmail(u.id, u.email); }} data-testid={`invite-${u.email}`}><Mail className="w-3 h-3" /></Button>}
                                {canEditMembers && !u.is_self && !u.is_owner && <Button variant="ghost" size="icon" className="h-7 w-7 text-amber-600 dark:text-amber-400" title="Reset account (forget user)" onClick={e => { e.stopPropagation(); setMemberToReset(u); }} data-testid={`reset-${u.email}`}><RotateCcw className="w-3 h-3" /></Button>}
                                {canEditMembers && u.can_delete && !u.is_self && !u.is_owner && <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" title="Remove" onClick={e => { e.stopPropagation(); deleteMember(u.id); }} data-testid={`delete-${u.email}`}><Trash2 className="w-3 h-3" /></Button>}
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                {/* Legacy Permissions Dialog removed — use Access & Permissions tab instead */}

                {/* Merge Dialog */}
                <Dialog open={showMerge} onOpenChange={setShowMerge}>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Merge Users</DialogTitle>
                      <DialogDescription>Merge a manually-added user into a workspace-synced user. The manual user's permissions and profile will transfer.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs text-muted-foreground block mb-1">Manual User (source)</label>
                        <Select value={mergeManual || '_none'} onValueChange={v => setMergeManual(v === '_none' ? '' : v)}>
                          <SelectTrigger className="h-9"><SelectValue placeholder="Select manual user..." /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">— Select —</SelectItem>
                            {allUsers.filter(u => u.source === 'manual').map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground block mb-1">Workspace User (target)</label>
                        <Select value={mergeWorkspace || '_none'} onValueChange={v => setMergeWorkspace(v === '_none' ? '' : v)}>
                          <SelectTrigger className="h-9"><SelectValue placeholder="Select workspace user..." /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">— Select —</SelectItem>
                            {allUsers.filter(u => u.source === 'workspace').map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => setShowMerge(false)}>Cancel</Button>
                        <Button size="sm" onClick={doMerge} disabled={!mergeManual || !mergeWorkspace} data-testid="confirm-merge-btn">Merge</Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            ) :

            /* Departments Tab */
            tab === 'departments' ? (
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold">Organisation Departments</h3>
                  <Button size="sm" onClick={() => { setEditingDept(null); setDeptForm({ name: '', description: '', head_user_id: '' }); setShowDeptForm(true); }} data-testid="add-dept-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" />Add Department
                  </Button>
                </div>
                {showDeptForm && (
                  <div className="bg-card border rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">Name *</label>
                        <Input value={deptForm.name} onChange={e => setDeptForm(p => ({ ...p, name: e.target.value }))} data-testid="dept-name-input" />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">Description</label>
                        <Input value={deptForm.description} onChange={e => setDeptForm(p => ({ ...p, description: e.target.value }))} />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">Department Head</label>
                        <Select value={deptForm.head_user_id || '_none'} onValueChange={v => setDeptForm(p => ({ ...p, head_user_id: v === '_none' ? '' : v }))}>
                          <SelectTrigger className="h-9"><SelectValue placeholder="Select..." /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">— None —</SelectItem>
                            {allUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={saveDept} data-testid="save-dept-btn">{editingDept ? 'Update' : 'Create'}</Button>
                      <Button size="sm" variant="outline" onClick={() => setShowDeptForm(false)}>Cancel</Button>
                    </div>
                  </div>
                )}
                <div className="bg-card rounded-xl border overflow-hidden">
                  <table className="w-full text-sm" data-testid="departments-table">
                    <thead className="bg-muted/50 border-b">
                      <tr>
                        <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">Department</th>
                        <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">Description</th>
                        <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">Head</th>
                        <th className="text-right px-4 py-2.5 font-medium text-muted-foreground text-xs w-24">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {departments.length === 0 ? <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground text-xs">No departments created yet</td></tr> :
                        departments.map(d => (
                          <tr key={d.department_id} className="hover:bg-accent/50 group">
                            <td className="px-4 py-3 font-medium">{d.name}</td>
                            <td className="px-4 py-3 text-muted-foreground text-xs">{d.description || '-'}</td>
                            <td className="px-4 py-3 text-xs">{d.head_user_id ? (allUsers.find(u => u.id === d.head_user_id)?.name || d.head_user_id) : '-'}</td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex gap-0.5 justify-end opacity-0 group-hover:opacity-100">
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditingDept(d); setDeptForm({ name: d.name, description: d.description || '', head_user_id: d.head_user_id || '' }); setShowDeptForm(true); }}><Pencil className="w-3 h-3" /></Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => deleteDept(d.department_id)}><Trash2 className="w-3 h-3" /></Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) :

            /* Org Chart Tab */
            tab === 'chart' ? (
              <ScrollArea className="h-full p-6">
                {chartData.length === 0 ? <div className="text-center py-16"><Network className="w-10 h-10 mx-auto text-muted-foreground/20 mb-2" /><p className="text-sm text-muted-foreground">Add reporting structure in People profiles to build the org chart</p></div> :
                  (() => {
                    const roots = chartData.filter(p => !p.reports_to || !chartData.some(c => c.user_id === p.reports_to));
                    return <div className="space-y-2">{roots.map(r => <ChartNode key={r.user_id} person={r} people={chartData} onSelect={p => { const match = allUsers.find(u => u.id === p.user_id || u.email === p.email); if (match) setSelectedPerson(match); }} />)}</div>;
                  })()}
              </ScrollArea>
            ) :

            /* Skills Matrix Tab */
            tab === 'skills' ? (
              <div className="p-6">
                {skillsMatrix.people.length === 0 ? <div className="text-center py-16"><GraduationCap className="w-10 h-10 mx-auto text-muted-foreground/20 mb-2" /><p className="text-sm text-muted-foreground">Add skills to team profiles to populate the matrix</p></div> :
                  <div className="bg-card rounded-xl border overflow-x-auto">
                    <table className="text-xs" data-testid="skills-matrix-table">
                      <thead className="bg-muted/50 border-b">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium text-muted-foreground sticky left-0 bg-muted/50 z-10 min-w-[140px]">Person</th>
                          {skillsMatrix.skills.map(s => <th key={s} className="text-center px-2 py-2 font-medium text-muted-foreground whitespace-nowrap">{s}</th>)}
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {skillsMatrix.people.map(p => (
                          <tr key={p.user_id} className="hover:bg-accent/50">
                            <td className="px-3 py-2 font-medium sticky left-0 bg-card z-10">{p.name}</td>
                            {skillsMatrix.skills.map(s => (
                              <td key={s} className="text-center px-2 py-2">
                                {p.skills?.includes(s) ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto" /> : <span className="text-muted-foreground/20">-</span>}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>}
              </div>
            ) :

            /* Access & Permissions Tab */
            tab === 'access' ? (
              <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="access-permissions-tab">
                {accessLoading ? (
                  <div className="flex items-center justify-center py-20"><Loader2 className="w-5 h-5 animate-spin mr-2 text-muted-foreground" />Loading access data...</div>
                ) : (
                  <>
                    {/* My Profile Banner */}
                    {myAccessProfile && (
                      <div className={cn("rounded-xl border p-4 flex items-center justify-between",
                        myAccessProfile.profile_key === 'admin' ? "bg-indigo-50/50 border-indigo-200" :
                        myAccessProfile.profile_key === 'finance_analyst' ? "bg-amber-50/50 border-amber-200" :
                        "bg-slate-50/50 border-slate-200"
                      )} data-testid="my-access-banner">
                        <div className="flex items-center gap-3">
                          <ShieldCheck className={cn("w-5 h-5",
                            myAccessProfile.profile_key === 'admin' ? "text-indigo-600" :
                            myAccessProfile.profile_key === 'finance_analyst' ? "text-amber-600" : "text-slate-500"
                          )} />
                          <div>
                            <p className="text-sm font-medium">Your Access: <span className="font-bold">{myAccessProfile.label}</span></p>
                            <p className="text-[11px] text-muted-foreground">
                              Source: {myAccessProfile.source === 'explicit_grant' ? 'Access Grant' : myAccessProfile.source === 'legacy_compatibility' ? 'Legacy Compatibility' : myAccessProfile.source}
                              {myAccessProfile.legacy_role && myAccessProfile.source === 'legacy_compatibility' && ` (role: ${myAccessProfile.legacy_role})`}
                            </p>
                          </div>
                        </div>
                        <Badge variant="outline" className="text-xs">{myAccessProfile.permissions?.length || 0} permissions</Badge>
                      </div>
                    )}

                    {/* Profile Cards + Create Button */}
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold">Permission Profiles</h3>
                      {canManagePermissions && (
                        <Button size="sm" onClick={() => { setProfileForm({ name: '', description: '', permission_keys: ['org.members.read', 'finance.workspaces.read'] }); setCreateProfileOpen(true); }} data-testid="create-profile-btn">
                          <Plus className="w-3.5 h-3.5 mr-1" />Create New Permission Profile
                        </Button>
                      )}
                    </div>
                    <div className={cn("grid gap-4", accessProfiles.length <= 3 ? "grid-cols-3" : accessProfiles.length === 4 ? "grid-cols-4" : "grid-cols-3")}>
                      {accessProfiles.map(profile => {
                        const isCustom = !profile.is_system;
                        const colorClass = profile.key === 'admin' ? "bg-indigo-500" :
                          profile.key === 'finance_analyst' ? "bg-amber-500" :
                          profile.key === 'user' ? "bg-slate-400" : "bg-violet-500";
                        return (
                          <Card key={profile.key} className="relative overflow-hidden" data-testid={`profile-card-${profile.key}`}>
                            <div className={cn("absolute top-0 left-0 right-0 h-1", colorClass)} />
                            <CardHeader className="pb-2 pt-4">
                              <div className="flex items-center justify-between">
                                <CardTitle className="text-sm flex items-center gap-1.5">
                                  {profile.label}
                                  {isCustom && <Badge variant="secondary" className="text-[9px] px-1.5 py-0">Custom</Badge>}
                                </CardTitle>
                                {profile.is_deletable && canManagePermissions && (
                                  <div className="flex items-center gap-0.5">
                                    {!profile.is_system && (
                                      <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-indigo-500"
                                        onClick={() => openEditProfile(profile)} data-testid={`edit-profile-${profile.key}`}>
                                        <Pencil className="w-3 h-3" />
                                      </Button>
                                    )}
                                    <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-red-500"
                                      onClick={() => openDeleteProfile(profile)} data-testid={`delete-profile-${profile.key}`}>
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                )}
                              </div>
                              <CardDescription className="text-[11px]">{profile.description}</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <div className="space-y-1">
                                {profile.permissions
                                  .filter(p => !(profile.key === 'user' && p === 'finance.staffing.read'))
                                  .slice(0, 6)
                                  .map(p => {
                                    const label = profile.key === 'user' && p === 'finance.workspaces.read'
                                      ? 'Access Personal Finance workspace only'
                                      : (profile.permission_labels?.[p] || p);
                                    return (
                                      <div key={p} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                                        <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />
                                        <span className="truncate">{label}</span>
                                      </div>
                                    );
                                  })}
                                {profile.permissions.length > 6 && (
                                  <p className="text-[10px] text-muted-foreground/60 pl-4.5">+{profile.permissions.length - 6} more</p>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>

                    {/* Member Access Table */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Users className="w-4 h-4" />Member Access Overview
                        </CardTitle>
                        <CardDescription className="text-[11px]">
                          Manage access profiles for organisation members. Explicit grants override legacy compatibility mapping.
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        {accessOverview.length === 0 ? (
                          <p className="text-center text-muted-foreground py-8 text-xs">No members found</p>
                        ) : (
                          <div className="border rounded-lg overflow-hidden">
                            <table className="w-full text-xs" data-testid="access-overview-table">
                              <thead className="bg-muted/50">
                                <tr>
                                  <th className="text-left p-3 font-medium text-muted-foreground">Member</th>
                                  <th className="text-left p-3 font-medium text-muted-foreground">Legacy Role</th>
                                  <th className="text-left p-3 font-medium text-muted-foreground">Effective Profile</th>
                                  <th className="text-left p-3 font-medium text-muted-foreground">Source</th>
                                  <th className="text-left p-3 font-medium text-muted-foreground">Permissions</th>
                                  <th className="text-right p-3 font-medium text-muted-foreground">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {accessOverview.map(member => (
                                  <tr key={member.membership_id} className="border-t hover:bg-accent/30 transition-colors" data-testid={`access-row-${member.membership_id}`}>
                                    <td className="p-3">
                                      <div className="flex items-center gap-2">
                                        {member.picture ? (
                                          <img src={member.picture} alt="" className="w-7 h-7 rounded-full" />
                                        ) : (
                                          <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-[10px] font-semibold text-indigo-600">
                                            {(member.name || '?')[0].toUpperCase()}
                                          </div>
                                        )}
                                        <div>
                                          <p className="font-medium text-xs">{member.name}</p>
                                          <p className="text-[10px] text-muted-foreground">{member.email}</p>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="p-3">
                                      <Badge variant="secondary" className="text-[10px]">{member.legacy_role}</Badge>
                                    </td>
                                    <td className="p-3">
                                      {editingAccess === member.membership_id ? (
                                        <Select defaultValue={member.profile_key} onValueChange={(v) => handleAssignProfile(member.membership_id, v)}>
                                          <SelectTrigger className="h-7 text-xs w-40" data-testid={`profile-select-${member.membership_id}`}><SelectValue /></SelectTrigger>
                                          <SelectContent>
                                            {accessProfiles.map(p => (
                                              <SelectItem key={p.key} value={p.key}>{p.label}{!p.is_system ? ' (Custom)' : ''}</SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      ) : (
                                        <Badge className={cn("text-[10px]",
                                          member.profile_key === 'admin' ? "bg-indigo-100 text-indigo-700 hover:bg-indigo-100" :
                                          member.profile_key === 'finance_analyst' ? "bg-amber-100 text-amber-700 hover:bg-amber-100" :
                                          member.profile_key === 'user' ? "bg-slate-100 text-slate-700 hover:bg-slate-100" :
                                          "bg-violet-100 text-violet-700 hover:bg-violet-100"
                                        )} data-testid={`profile-badge-${member.membership_id}`}>{member.profile_label}</Badge>
                                      )}
                                    </td>
                                    <td className="p-3">
                                      <span className={cn("inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full",
                                        member.source === 'explicit_grant'
                                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                          : "bg-slate-50 text-slate-600 border border-slate-200"
                                      )} data-testid={`source-badge-${member.membership_id}`}>
                                        {member.source === 'explicit_grant' ? (
                                          <><ShieldCheck className="w-3 h-3" />Access Grant</>
                                        ) : (
                                          <><History className="w-3 h-3" />Legacy Compatibility</>
                                        )}
                                      </span>
                                    </td>
                                    <td className="p-3">
                                      <div className="flex items-center gap-2">
                                        <span className="text-[10px] text-muted-foreground">{member.permissions?.length || 0} perms</span>
                                        {member.is_protected && <Shield className="w-3 h-3 text-indigo-500" title="Protected Owner" />}
                                        {member.expires_at && (
                                          <span className="text-[9px] text-amber-600 flex items-center gap-0.5"><CalendarDays className="w-2.5 h-2.5" />Expires</span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="p-3 text-right">
                                      <div className="flex items-center justify-end gap-1">
                                        {(() => {
                                          const isSelf = member.user_id && member.user_id === myUserId;
                                          const isOwnerTarget = member.is_protected;
                                          const actorIsOwner = myAccessProfile?.is_owner;
                                          const canEdit = canManagePermissions && !isSelf && (!isOwnerTarget || actorIsOwner);
                                          const canRevoke = canManagePermissions && !isSelf && member.has_explicit_grant && (!isOwnerTarget || actorIsOwner);

                                          return (
                                            <>
                                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openTraceModal(member.membership_id)} data-testid={`info-btn-${member.membership_id}`} title="Grant details">
                                                <Info className="w-3.5 h-3.5" />
                                              </Button>
                                              {editingAccess === member.membership_id ? (
                                                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setEditingAccess(null)} data-testid={`cancel-edit-${member.membership_id}`}>
                                                  <X className="w-3.5 h-3.5" />
                                                </Button>
                                              ) : canEdit ? (
                                                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setEditingAccess(member.membership_id)} data-testid={`edit-access-${member.membership_id}`}>
                                                  <Pencil className="w-3.5 h-3.5" />
                                                </Button>
                                              ) : null}
                                              {canRevoke && (
                                                <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => handleRevokeGrant(member.membership_id)} data-testid={`revoke-grant-${member.membership_id}`}>
                                                  <Trash2 className="w-3.5 h-3.5" />
                                                </Button>
                                              )}
                                              {isSelf && <span className="text-[10px] text-muted-foreground italic">You</span>}
                                            </>
                                          );
                                        })()}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* ===== GRANT TRACEABILITY MODAL ===== */}
                    <Dialog open={!!traceModal} onOpenChange={() => setTraceModal(null)}>
                      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="trace-modal">
                        <DialogHeader>
                          <DialogTitle className="text-base flex items-center gap-2"><Info className="w-4 h-4" />Grant Traceability</DialogTitle>
                          <DialogDescription className="text-xs">Full audit trail for this member's access.</DialogDescription>
                        </DialogHeader>
                        {traceLoading ? (
                          <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" /></div>
                        ) : traceData ? (
                          <div className="space-y-4">
                            {/* Grantee */}
                            <div className="bg-muted/30 rounded-lg p-3 space-y-1">
                              <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Grantee</p>
                              <div className="flex items-center gap-2">
                                {traceData.grantee?.picture ? (
                                  <img src={traceData.grantee.picture} alt="" className="w-8 h-8 rounded-full" />
                                ) : (
                                  <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-600">
                                    {(traceData.grantee?.name || '?')[0].toUpperCase()}
                                  </div>
                                )}
                                <div>
                                  <p className="text-sm font-medium">{traceData.grantee?.name}</p>
                                  <p className="text-[11px] text-muted-foreground">{traceData.grantee?.email}</p>
                                </div>
                              </div>
                              <p className="text-[11px] text-muted-foreground">Legacy role: <span className="font-medium">{traceData.grantee?.legacy_role}</span></p>
                            </div>

                            {/* Active Profile */}
                            <div className="bg-muted/30 rounded-lg p-3 space-y-1">
                              <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Active Access Profile</p>
                              <div className="flex items-center gap-2">
                                <Badge className={cn("text-xs",
                                  traceData.active_profile?.access_profile_id === 'admin' ? "bg-indigo-100 text-indigo-700" :
                                  traceData.active_profile?.access_profile_id === 'finance_analyst' ? "bg-amber-100 text-amber-700" :
                                  traceData.active_profile?.access_profile_id === 'user' ? "bg-slate-100 text-slate-700" :
                                  "bg-violet-100 text-violet-700"
                                )}>{traceData.active_profile?.label}</Badge>
                                {traceData.active_profile?.is_system === false && <Badge variant="outline" className="text-[9px]">Custom</Badge>}
                              </div>
                              <p className="text-[11px] text-muted-foreground">{traceData.active_profile?.description}</p>
                              <p className="text-[11px] text-muted-foreground">{traceData.active_profile?.permissions?.length || 0} permissions</p>
                            </div>

                            {/* Source */}
                            <div className="bg-muted/30 rounded-lg p-3 space-y-1">
                              <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Source</p>
                              <span className={cn("inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium",
                                traceData.source === 'explicit_grant'
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                  : "bg-slate-50 text-slate-600 border border-slate-200"
                              )}>
                                {traceData.source === 'explicit_grant' ? <><ShieldCheck className="w-3.5 h-3.5" />Access Grant</> : <><History className="w-3.5 h-3.5" />Legacy Compatibility</>}
                              </span>
                              {traceData.legacy_mapping && (
                                <p className="text-[11px] text-muted-foreground mt-1">{traceData.legacy_mapping.explanation}</p>
                              )}
                            </div>

                            {/* Grant Details (if explicit) */}
                            {traceData.has_explicit_grant && traceData.grant && (
                              <div className="bg-muted/30 rounded-lg p-3 space-y-2">
                                <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Grant Details</p>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                                  <span className="text-muted-foreground">Grant ID</span>
                                  <span className="font-mono text-[10px]">{traceData.grant.grant_id}</span>
                                  <span className="text-muted-foreground">Granted at</span>
                                  <span>{traceData.grant.granted_at ? new Date(traceData.grant.granted_at).toLocaleString() : '-'}</span>
                                  <span className="text-muted-foreground">Expires at</span>
                                  <span>{traceData.grant.expires_at ? new Date(traceData.grant.expires_at).toLocaleString() : 'No expiration'}</span>
                                  <span className="text-muted-foreground">Reason</span>
                                  <span>{traceData.grant.reason || '-'}</span>
                                </div>
                                {traceData.changed_by && (
                                  <div className="text-[11px]">
                                    <span className="text-muted-foreground">Changed by: </span>
                                    <span className="font-medium">{traceData.changed_by.name}</span>
                                    <span className="text-muted-foreground"> ({traceData.changed_by.email})</span>
                                  </div>
                                )}
                              </div>
                            )}

                            {!traceData.has_explicit_grant && (
                              <div className="bg-amber-50/50 border border-amber-200 rounded-lg p-3 text-[11px] text-amber-700">
                                No explicit grant exists. This member resolves via legacy compatibility mapping.
                              </div>
                            )}

                            {/* Grant History */}
                            <div>
                              <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-2">Grant History</p>
                              {(!traceData.history || traceData.history.length === 0) ? (
                                <p className="text-[11px] text-muted-foreground py-2">No history events recorded yet.</p>
                              ) : (
                                <div className="space-y-2">
                                  {traceData.history.map((evt, i) => (
                                    <div key={evt.event_id || i} className="bg-muted/20 rounded-lg p-2.5 border text-[11px] space-y-0.5">
                                      <div className="flex items-center justify-between">
                                        <Badge variant="outline" className="text-[9px]">{evt.event_type}</Badge>
                                        <span className="text-[10px] text-muted-foreground">{evt.timestamp ? new Date(evt.timestamp).toLocaleString() : ''}</span>
                                      </div>
                                      {evt.old_access_profile_id_label && evt.new_access_profile_id_label && (
                                        <p className="text-muted-foreground">{evt.old_access_profile_id_label} &rarr; {evt.new_access_profile_id_label}</p>
                                      )}
                                      {!evt.old_access_profile_id_label && evt.new_access_profile_id_label && (
                                        <p className="text-muted-foreground">&rarr; {evt.new_access_profile_id_label}</p>
                                      )}
                                      {evt.old_access_profile_id_label && !evt.new_access_profile_id_label && (
                                        <p className="text-muted-foreground">{evt.old_access_profile_id_label} &rarr; (revoked)</p>
                                      )}
                                      <p className="text-muted-foreground">By: {evt.changed_by_name}{evt.reason ? ` — ${evt.reason}` : ''}</p>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Delegation-Derived Access */}
                            {traceData.delegations && (traceData.delegations.incoming?.length > 0 || traceData.delegations.outgoing?.length > 0) && (
                              <div>
                                <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-2">Delegation Access</p>
                                <div className="space-y-3">
                                  {traceData.delegations.incoming?.length > 0 && (
                                    <div className="space-y-2">
                                      <p className="text-[11px] font-medium text-blue-700 dark:text-blue-400">Incoming (receives access from)</p>
                                      {traceData.delegations.incoming.map((d) => (
                                        <div key={d.delegation_id} className="bg-blue-50/50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-2.5 text-[11px] space-y-1" data-testid={`delegation-incoming-${d.delegation_id}`}>
                                          <div className="flex items-center justify-between">
                                            <span className="font-medium">{d.from_membership_id_name || d.from_membership_id}</span>
                                            <Badge variant="outline" className="text-[9px]">{d.delegation_scope_type}</Badge>
                                          </div>
                                          <div className="flex flex-wrap gap-1">
                                            {d.profiles?.map((p) => (
                                              <Badge key={p.access_profile_id} className="text-[9px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">{p.label}</Badge>
                                            ))}
                                          </div>
                                          {(d.starts_at || d.ends_at) && (
                                            <p className="text-muted-foreground text-[10px]">
                                              {d.starts_at ? `From ${new Date(d.starts_at).toLocaleDateString()}` : ''}
                                              {d.ends_at ? ` until ${new Date(d.ends_at).toLocaleDateString()}` : ' (no expiry)'}
                                            </p>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  {traceData.delegations.outgoing?.length > 0 && (
                                    <div className="space-y-2">
                                      <p className="text-[11px] font-medium text-amber-700 dark:text-amber-400">Outgoing (delegates to)</p>
                                      {traceData.delegations.outgoing.map((d) => (
                                        <div key={d.delegation_id} className="bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-2.5 text-[11px] space-y-1" data-testid={`delegation-outgoing-${d.delegation_id}`}>
                                          <div className="flex items-center justify-between">
                                            <span className="font-medium">{d.to_membership_id_name || d.to_membership_id}</span>
                                            <Badge variant="outline" className="text-[9px]">{d.delegation_scope_type}</Badge>
                                          </div>
                                          <div className="flex flex-wrap gap-1">
                                            {d.profiles?.map((p) => (
                                              <Badge key={p.access_profile_id} className="text-[9px] bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300">{p.label}</Badge>
                                            ))}
                                          </div>
                                          {(d.starts_at || d.ends_at) && (
                                            <p className="text-muted-foreground text-[10px]">
                                              {d.starts_at ? `From ${new Date(d.starts_at).toLocaleDateString()}` : ''}
                                              {d.ends_at ? ` until ${new Date(d.ends_at).toLocaleDateString()}` : ' (no expiry)'}
                                            </p>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-center text-muted-foreground py-6 text-xs">No data available</p>
                        )}
                      </DialogContent>
                    </Dialog>

                    {/* ===== CREATE PROFILE MODAL ===== */}
                    <Dialog open={createProfileOpen} onOpenChange={setCreateProfileOpen}>
                      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="create-profile-modal">
                        <DialogHeader>
                          <DialogTitle className="text-base">Create New Permission Profile</DialogTitle>
                          <DialogDescription className="text-xs">Define a custom permission bundle. Only Finance permissions are enforced by the backend in this release.</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Profile Name *</label>
                              <Input value={profileForm.name} onChange={e => setProfileForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., Marketing Lead" className="h-8 text-sm" data-testid="profile-name-input" />
                            </div>
                            <div>
                              <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Description</label>
                              <Input value={profileForm.description} onChange={e => setProfileForm(p => ({ ...p, description: e.target.value }))} placeholder="Brief description" className="h-8 text-sm" />
                            </div>
                          </div>

                          {/* Permission sections */}
                          {[
                            { id: 'finance', label: 'Finance', enforced: true, keys: [
                              { key: 'finance.workspaces.read', label: 'View finance workspaces' },
                              { key: 'finance.workspaces.read_org', label: 'View organisation finance workspaces' },
                              { key: 'finance.workspaces.create', label: 'Create finance workspaces' },
                              { key: 'finance.workspaces.manage', label: 'Rename or delete finance workspaces' },
                              { key: 'finance.staffing.read', label: 'View staffing data and entries' },
                              { key: 'finance.staffing.write', label: 'Create, update, delete staffing entries' },
                              { key: 'finance.staffing.sync', label: 'Sync staffing from people module' },
                              { key: 'finance.staffing.promote', label: 'Promote contractor to organisation people' },
                              { key: 'finance.staffing.link', label: 'Link staffing entry to existing person' },
                            ]},
                            { id: 'org', label: 'Organization', enforced: false, keys: [
                              { key: 'org.members.read', label: 'View member list' },
                              { key: 'org.members.create', label: 'Add members' },
                              { key: 'org.members.edit', label: 'Edit members' },
                              { key: 'org.members.delete', label: 'Remove members' },
                              { key: 'org.settings.update', label: 'Modify org settings' },
                            ]},
                            { id: 'analytics', label: 'Analytics', enforced: false, keys: [
                              { key: 'analytics.dashboards.read', label: 'View analytics dashboards' },
                              { key: 'analytics.reports.export', label: 'Export analytics reports' },
                            ]},
                            { id: 'tasks', label: 'Tasks', enforced: false, keys: [
                              { key: 'tasks.read', label: 'View tasks' },
                              { key: 'tasks.write', label: 'Create and edit tasks' },
                              { key: 'tasks.delete', label: 'Delete tasks' },
                              { key: 'tasks.assign', label: 'Assign tasks to members' },
                            ]},
                            { id: 'ai', label: 'AI', enforced: false, keys: [
                              { key: 'ai.assistant.use', label: 'Use AI assistant' },
                              { key: 'ai.workflows.manage', label: 'Manage AI workflows' },
                            ]},
                            { id: 'recruitment', label: 'Recruitment', enforced: false, keys: [
                              { key: 'recruitment.jobs.read', label: 'View job listings' },
                              { key: 'recruitment.jobs.write', label: 'Create and manage jobs' },
                              { key: 'recruitment.applicants.read', label: 'View applicants' },
                              { key: 'recruitment.applicants.write', label: 'Manage applicants' },
                            ]},
                          ].map(section => {
                            const sectionKeys = section.keys.map(k => k.key);
                            const selectedCount = sectionKeys.filter(k => profileForm.permission_keys.includes(k)).length;
                            const allSelected = selectedCount === sectionKeys.length;
                            return (
                              <div key={section.id} className="border rounded-lg overflow-hidden" data-testid={`perm-section-${section.id}`}>
                                <div className={cn("flex items-center justify-between px-3 py-2 cursor-pointer",
                                  section.enforced ? "bg-emerald-50/50" : "bg-muted/30"
                                )} onClick={() => toggleSection(sectionKeys, allSelected)}>
                                  <div className="flex items-center gap-2">
                                    <Checkbox checked={allSelected} className="h-3.5 w-3.5" />
                                    <span className="text-xs font-medium">{section.label}</span>
                                    {section.enforced ? (
                                      <Badge variant="outline" className="text-[9px] border-emerald-300 text-emerald-700">Enforced</Badge>
                                    ) : (
                                      <Badge variant="outline" className="text-[9px] border-slate-300 text-slate-500 flex items-center gap-0.5"><Lock className="w-2.5 h-2.5" />Coming Soon</Badge>
                                    )}
                                  </div>
                                  <span className="text-[10px] text-muted-foreground">{selectedCount}/{sectionKeys.length}</span>
                                </div>
                                <div className="px-3 py-2 space-y-1.5 border-t">
                                  {section.keys.map(perm => (
                                    <label key={perm.key} className="flex items-center gap-2 cursor-pointer hover:bg-accent/30 rounded px-1 py-0.5">
                                      <Checkbox checked={profileForm.permission_keys.includes(perm.key)} className="h-3.5 w-3.5"
                                        onCheckedChange={() => togglePermission(perm.key)} />
                                      <span className="text-[11px] text-muted-foreground">{perm.label}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <DialogFooter className="mt-4">
                          <Button variant="outline" size="sm" onClick={() => setCreateProfileOpen(false)}>Cancel</Button>
                          <Button size="sm" onClick={handleCreateProfile} disabled={creatingProfile} data-testid="save-profile-btn">
                            {creatingProfile && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}Create Profile
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>

                    {/* ===== DELETE PROFILE MODAL ===== */}
                    <Dialog open={!!deleteProfileTarget} onOpenChange={() => setDeleteProfileTarget(null)}>
                      <DialogContent className="max-w-md" data-testid="delete-profile-modal">
                        <DialogHeader>
                          <DialogTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" />Delete Permission Profile</DialogTitle>
                          <DialogDescription className="text-xs">
                            You are about to delete <span className="font-semibold">{deleteProfileTarget?.label}</span>.
                          </DialogDescription>
                        </DialogHeader>
                        {deleteProfileLoading ? (
                          <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin" /></div>
                        ) : (
                          <div className="space-y-3">
                            {deleteProfileMembers.length > 0 ? (
                              <>
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                                  <p className="font-medium mb-1">{deleteProfileMembers.length} member(s) currently assigned to this profile:</p>
                                  <ul className="space-y-0.5 ml-2">
                                    {deleteProfileMembers.map(m => (
                                      <li key={m.membership_id} className="text-[11px]">{m.name} ({m.email})</li>
                                    ))}
                                  </ul>
                                  <p className="mt-2 text-[11px]">They will be automatically downgraded to the <span className="font-semibold">User</span> profile. This action is traceable in grant history.</p>
                                </div>
                              </>
                            ) : (
                              <p className="text-xs text-muted-foreground">No members are currently assigned to this profile. It can be safely deleted.</p>
                            )}
                          </div>
                        )}
                        <DialogFooter>
                          <Button variant="outline" size="sm" onClick={() => setDeleteProfileTarget(null)}>Cancel</Button>
                          <Button variant="destructive" size="sm" onClick={handleDeleteProfile} disabled={deleteProfileLoading} data-testid="confirm-delete-profile-btn">
                            {deleteProfileLoading && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
                            Accept and Delete Profile
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>

                    {/* ===== EDIT PROFILE MODAL ===== */}
                    <Dialog open={!!editProfileTarget} onOpenChange={() => setEditProfileTarget(null)}>
                      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="edit-profile-modal">
                        <DialogHeader>
                          <DialogTitle className="text-base flex items-center gap-2"><Pencil className="w-4 h-4" />Edit Permission Profile</DialogTitle>
                          <DialogDescription className="text-xs">Update permissions for <span className="font-semibold">{editProfileTarget?.label}</span>. Only Finance permissions are enforced by the backend in this release.</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Profile Name</label>
                              <Input value={editProfileForm.name} onChange={e => setEditProfileForm(p => ({ ...p, name: e.target.value }))} className="h-8 text-sm" data-testid="edit-profile-name-input" />
                            </div>
                            <div>
                              <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Description</label>
                              <Input value={editProfileForm.description} onChange={e => setEditProfileForm(p => ({ ...p, description: e.target.value }))} className="h-8 text-sm" />
                            </div>
                          </div>

                          {[
                            { id: 'finance', label: 'Finance', enforced: true, keys: [
                              { key: 'finance.workspaces.read', label: 'View finance workspaces' },
                              { key: 'finance.workspaces.read_org', label: 'View organisation finance workspaces' },
                              { key: 'finance.workspaces.create', label: 'Create finance workspaces' },
                              { key: 'finance.workspaces.manage', label: 'Rename or delete finance workspaces' },
                              { key: 'finance.staffing.read', label: 'View staffing data and entries' },
                              { key: 'finance.staffing.write', label: 'Create, update, delete staffing entries' },
                              { key: 'finance.staffing.sync', label: 'Sync staffing from people module' },
                              { key: 'finance.staffing.promote', label: 'Promote contractor to organisation people' },
                              { key: 'finance.staffing.link', label: 'Link staffing entry to existing person' },
                            ]},
                            { id: 'org', label: 'Organization', enforced: false, keys: [
                              { key: 'org.members.read', label: 'View member list' },
                              { key: 'org.members.create', label: 'Add members' },
                              { key: 'org.members.edit', label: 'Edit members' },
                              { key: 'org.members.delete', label: 'Remove members' },
                              { key: 'org.settings.update', label: 'Modify org settings' },
                            ]},
                            { id: 'analytics', label: 'Analytics', enforced: false, keys: [
                              { key: 'analytics.dashboards.read', label: 'View analytics dashboards' },
                              { key: 'analytics.reports.export', label: 'Export analytics reports' },
                            ]},
                            { id: 'tasks', label: 'Tasks', enforced: false, keys: [
                              { key: 'tasks.read', label: 'View tasks' },
                              { key: 'tasks.write', label: 'Create and edit tasks' },
                              { key: 'tasks.delete', label: 'Delete tasks' },
                              { key: 'tasks.assign', label: 'Assign tasks to members' },
                            ]},
                            { id: 'ai', label: 'AI', enforced: false, keys: [
                              { key: 'ai.assistant.use', label: 'Use AI assistant' },
                              { key: 'ai.workflows.manage', label: 'Manage AI workflows' },
                            ]},
                            { id: 'recruitment', label: 'Recruitment', enforced: false, keys: [
                              { key: 'recruitment.jobs.read', label: 'View job listings' },
                              { key: 'recruitment.jobs.write', label: 'Create and manage jobs' },
                              { key: 'recruitment.applicants.read', label: 'View applicants' },
                              { key: 'recruitment.applicants.write', label: 'Manage applicants' },
                            ]},
                          ].map(section => {
                            const sectionKeys = section.keys.map(k => k.key);
                            const selectedCount = sectionKeys.filter(k => editProfileForm.permission_keys.includes(k)).length;
                            const allSelected = selectedCount === sectionKeys.length;
                            return (
                              <div key={section.id} className="border rounded-lg overflow-hidden" data-testid={`edit-perm-section-${section.id}`}>
                                <div className={cn("flex items-center justify-between px-3 py-2 cursor-pointer",
                                  section.enforced ? "bg-emerald-50/50" : "bg-muted/30"
                                )} onClick={() => toggleEditSection(sectionKeys, allSelected)}>
                                  <div className="flex items-center gap-2">
                                    <Checkbox checked={allSelected} className="h-3.5 w-3.5" />
                                    <span className="text-xs font-medium">{section.label}</span>
                                    {section.enforced ? (
                                      <Badge variant="outline" className="text-[9px] border-emerald-300 text-emerald-700">Enforced</Badge>
                                    ) : (
                                      <Badge variant="outline" className="text-[9px] border-slate-300 text-slate-500 flex items-center gap-0.5"><Lock className="w-2.5 h-2.5" />Coming Soon</Badge>
                                    )}
                                  </div>
                                  <span className="text-[10px] text-muted-foreground">{selectedCount}/{sectionKeys.length}</span>
                                </div>
                                <div className="px-3 py-2 space-y-1.5 border-t">
                                  {section.keys.map(perm => {
                                    const isChecked = editProfileForm.permission_keys.includes(perm.key);
                                    const wasOriginal = editProfileOriginalKeys.has(perm.key);
                                    const isAdded = isChecked && !wasOriginal;
                                    const isRemoved = !isChecked && wasOriginal;
                                    return (
                                      <label key={perm.key} className="flex items-center gap-2 cursor-pointer hover:bg-accent/30 rounded px-1 py-0.5">
                                        <Checkbox checked={isChecked} className="h-3.5 w-3.5"
                                          onCheckedChange={() => toggleEditPermission(perm.key)} />
                                        <span className={cn("text-[11px]", isAdded ? "text-emerald-700 font-medium" : isRemoved ? "text-red-500 line-through" : "text-muted-foreground")}>{perm.label}</span>
                                        {isAdded && <Badge className="text-[8px] px-1 py-0 bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Added</Badge>}
                                        {isRemoved && <Badge className="text-[8px] px-1 py-0 bg-red-100 text-red-600 hover:bg-red-100">Removed</Badge>}
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}

                          {/* Affected users warning */}
                          {editProfileMembers.length > 0 && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800" data-testid="edit-profile-warning">
                              <p className="font-medium mb-1">Updating this Permission Profile will affect the following users. Check carefully before proceeding.</p>
                              <ul className="space-y-0.5 ml-2">
                                {editProfileMembers.map(m => (
                                  <li key={m.membership_id} className="text-[11px]">{m.name}{m.email ? ` (${m.email})` : ''}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                        <DialogFooter className="mt-4">
                          <Button variant="outline" size="sm" onClick={() => setEditProfileTarget(null)}>Cancel</Button>
                          <Button size="sm" onClick={handleSaveProfile} disabled={savingProfile} data-testid="save-edit-profile-btn">
                            {savingProfile && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}Save Changes
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </>
                )}
              </div>
            ) :

            /* Manage Org Tab */
            tab === 'manage' ? (
              <div className="p-6 max-w-3xl mx-auto space-y-6">
                {/* Workspace Connection */}
                <div className={cn("rounded-xl border p-4 flex items-center justify-between", wsStatus?.connected ? "bg-emerald-50/50 border-emerald-200" : "bg-muted/30")}>
                  <div className="flex items-center gap-3">
                    {wsStatus?.connected ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <Link2 className="w-5 h-5 text-muted-foreground" />}
                    <div>
                      <p className="text-sm font-medium">{wsStatus?.connected ? `Google Workspace (${wsStatus.domain})` : 'Google Workspace not connected'}</p>
                      <p className="text-[11px] text-muted-foreground">{wsStatus?.connected ? `Last sync: ${wsStatus.last_sync ? new Date(wsStatus.last_sync).toLocaleString() : 'Never'}` : 'Connect in Settings to auto-populate org info'}</p>
                    </div>
                  </div>
                  {wsStatus?.connected ? (
                    <div className="flex items-center gap-2"><span className="text-xs text-muted-foreground">Auto-create</span><Switch checked={wsStatus.auto_create_users} onCheckedChange={async (v) => { await fetch(`${API}/org/workspace/settings`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ auto_create_users: v }) }); setWsStatus(p => ({...p, auto_create_users: v})); }} /></div>
                  ) : <Button variant="outline" size="sm" onClick={() => navigate('/settings?tab=integrations')}><Settings className="w-3.5 h-3.5 mr-1" />Integrations</Button>}
                </div>

                {/* Org Profile */}
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Building2 className="w-4 h-4" />Organisation Profile</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    {/* Logo */}
                    <div className="flex items-center gap-4">
                      <div className="relative group">
                        {orgForm.logo_url ? <img src={orgForm.logo_url} alt="" className="w-16 h-16 rounded-xl object-cover border" /> :
                          <div className="w-16 h-16 rounded-xl bg-muted flex items-center justify-center border border-dashed"><Image className="w-6 h-6 text-muted-foreground/40" /></div>}
                        <label className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-xl opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                          <Upload className="w-5 h-5 text-white" />
                          <input type="file" accept="image/*" onChange={uploadLogo} className="hidden" />
                        </label>
                      </div>
                      <div><p className="text-sm font-medium">Logo</p><p className="text-[11px] text-muted-foreground">Click to upload (max 2MB)</p></div>
                      {uploadingLogo && <Loader2 className="w-4 h-4 animate-spin" />}
                    </div>
                    <Separator />
                    <div className="grid grid-cols-2 gap-4">
                      <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Name</label><Input value={orgForm.name || ''} onChange={e => setOrgForm(f => ({...f, name: e.target.value}))} className="h-8 text-sm" data-testid="org-name-field" /></div>
                      <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Industry</label><Input value={orgForm.industry || ''} onChange={e => setOrgForm(f => ({...f, industry: e.target.value}))} className="h-8 text-sm" placeholder="e.g., Technology" /></div>
                      <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Website</label><Input value={orgForm.website || ''} onChange={e => setOrgForm(f => ({...f, website: e.target.value}))} className="h-8 text-sm" placeholder="https://" /></div>
                      <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Phone</label><Input value={orgForm.phone || ''} onChange={e => setOrgForm(f => ({...f, phone: e.target.value}))} className="h-8 text-sm" /></div>
                      <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Size</label>
                        <Select value={orgForm.size || ''} onValueChange={v => setOrgForm(f => ({...f, size: v}))}>
                          <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent><SelectItem value="1-10">1-10</SelectItem><SelectItem value="11-50">11-50</SelectItem><SelectItem value="51-200">51-200</SelectItem><SelectItem value="201-500">201-500</SelectItem><SelectItem value="500+">500+</SelectItem></SelectContent>
                        </Select></div>
                      <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Founded</label><Input type="date" value={orgForm.founded || ''} onChange={e => setOrgForm(f => ({...f, founded: e.target.value}))} className="h-8 text-sm font-mono" /></div>
                    </div>
                    <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Address</label><Input value={orgForm.address || ''} onChange={e => setOrgForm(f => ({...f, address: e.target.value}))} className="h-8 text-sm" /></div>
                    <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Description</label><Textarea value={orgForm.description || ''} onChange={e => setOrgForm(f => ({...f, description: e.target.value}))} rows={3} className="text-sm" placeholder="About your organisation..." /></div>
                    <div><label className="text-[11px] font-medium text-muted-foreground mb-1 block">Context Notes (for AI)</label><Textarea value={orgForm.context_notes || ''} onChange={e => setOrgForm(f => ({...f, context_notes: e.target.value}))} rows={3} className="text-sm" placeholder="Key context, priorities, culture notes that AI should know about..." /></div>
                    <Button onClick={saveOrgInfo} className="w-full" size="sm" data-testid="save-org-btn"><Save className="w-3.5 h-3.5 mr-1" />Save Changes</Button>
                  </CardContent>
                </Card>

                {/* Files */}
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><FileText className="w-4 h-4" />Organisation Files</CardTitle><CardDescription>Upload policies, handbooks, org charts, and other key documents</CardDescription></CardHeader>
                  <CardContent>
                    <div className="mb-3"><label className="flex items-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer hover:bg-accent/30 transition-colors">
                      <Upload className="w-4 h-4 text-muted-foreground" /><span className="text-xs text-muted-foreground">Upload file (max 5MB)</span>
                      <input type="file" onChange={uploadOrgFile} className="hidden" />
                    </label></div>
                    {orgFiles.length === 0 ? <p className="text-xs text-muted-foreground text-center py-4">No files uploaded yet</p> :
                      <div className="space-y-1">{orgFiles.map(f => (
                        <div key={f.file_id} className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-accent/30">
                          <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-muted-foreground" /><span className="text-xs font-medium">{f.filename}</span><span className="text-[10px] text-muted-foreground">{(f.size / 1024).toFixed(0)}KB</span></div>
                          <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => deleteOrgFile(f.file_id)}><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      ))}</div>}
                  </CardContent>
                </Card>
              </div>
            ) :

            /* Delegations Tab */
            tab === 'delegations' ? (
              <div className="p-6 max-w-3xl mx-auto space-y-4">
                <div className="flex justify-end"><Button size="sm" onClick={() => setShowCreateDelegation(true)} data-testid="create-delegation-btn"><Plus className="w-4 h-4 mr-1" />Add Delegation</Button></div>
                <Card><CardContent className="pt-6">
                  {(delegations.as_principal || []).length === 0 && (delegations.as_supporter || []).length === 0 ? <p className="text-center text-muted-foreground py-6 text-xs">No delegations configured</p> :
                    <div className="space-y-4">
                      {(delegations.as_principal || []).length > 0 && <div><p className="text-sm font-medium mb-2">People who can act on your behalf</p><div className="space-y-2">{(delegations.as_principal || []).map(d => (
                        <div key={d.delegation_id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg" data-testid={`delegation-principal-${d.delegation_id}`}>
                          <div><p className="font-medium text-sm">{d.to_membership_id}</p><p className="text-xs text-muted-foreground">{(d.access_profile_ids || []).length} profile(s) &middot; {d.delegation_scope_type || 'all_tasks'}</p></div>
                          <Button variant="ghost" size="icon" className="text-red-500 h-7 w-7" onClick={() => revokeDelegation(d.delegation_id)} data-testid={`revoke-${d.delegation_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>))}</div></div>}
                      {(delegations.as_supporter || []).length > 0 && <div><p className="text-sm font-medium mb-2">People whose tasks you can manage</p><div className="space-y-2">{(delegations.as_supporter || []).map(d => (
                        <div key={d.delegation_id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg" data-testid={`delegation-supporter-${d.delegation_id}`}>
                          <p className="font-medium text-sm">{d.from_membership_id}</p><p className="text-xs text-muted-foreground">{(d.access_profile_ids || []).length} profile(s) &middot; {d.delegation_scope_type || 'all_tasks'}</p>
                        </div>))}</div></div>}
                    </div>}
                </CardContent></Card>
              </div>
            ) :

            /* Audit Tab */
            tab === 'audit' ? (
              <div className="p-6">
                <div className="bg-card rounded-xl border overflow-hidden">
                  <table className="w-full text-sm" data-testid="audit-log-table">
                    <thead className="bg-muted/50 border-b"><tr><th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">Time</th><th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">Action</th><th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">Details</th></tr></thead>
                    <tbody className="divide-y">
                      {auditLogs.length === 0 ? <tr><td colSpan={3} className="px-4 py-8 text-center text-muted-foreground text-xs">No audit logs yet</td></tr> :
                        auditLogs.map(l => <tr key={l.audit_id} className="hover:bg-accent/50"><td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{new Date(l.timestamp).toLocaleString()}</td><td className="px-4 py-2.5"><Badge variant="outline" className="text-xs font-mono">{l.action}</Badge></td><td className="px-4 py-2.5 text-xs max-w-xs truncate">{l.details}</td></tr>)}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </div>

          {/* Person Detail Sidebar */}
          {selectedPerson && (
            <PersonDetail person={selectedPerson} profile={personProfile} reviews={personReviews} leave={personLeave}
              onClose={() => setSelectedPerson(null)} onSaveProfile={saveProfile} onAddReview={addReview}
              onAddLeave={addLeave} onUpdateLeaveStatus={updateLeaveStatus} allMembers={allUsers}
              departments={departments}
              canEdit={canEditMembers}
              onChangeRole={(p, prof) => {
                setChangeRolePerson(p);
                setChangeRoleForm({ new_role: '', new_department: prof?.department || '', effective_date: new Date().toISOString().slice(0, 10) });
              }} />
          )}
        </div>
      </div>

      {/* Add User Dialog */}
      <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
        <DialogContent><DialogHeader><DialogTitle>Add User</DialogTitle><DialogDescription>Add a team member manually</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <Input placeholder="Email address" value={newUserEmail} onChange={(e) => setNewUserEmail(e.target.value)} data-testid="add-user-email" />
            <Input placeholder="Full name (optional)" value={newUserName} onChange={(e) => setNewUserName(e.target.value)} data-testid="add-user-name" />
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Permission Profile</label>
              <Select value={newUserRole} onValueChange={setNewUserRole}>
                <SelectTrigger className="h-9" data-testid="add-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {accessProfiles.map(p => (
                    <SelectItem key={p.key} value={p.key}>{p.label}{!p.is_system ? ' (Custom)' : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <input type="checkbox" id="send-invite" checked={sendInvite} onChange={e => setSendInvite(e.target.checked)} className="rounded border-gray-300" data-testid="send-invite-checkbox" />
              <label htmlFor="send-invite" className="text-xs text-muted-foreground">Send invite email to this user</label>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowAddUser(false)}>Cancel</Button><Button onClick={addManualUser} data-testid="confirm-add-user">Add User</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Delegation Dialog */}
      <Dialog open={showCreateDelegation} onOpenChange={setShowCreateDelegation}>
        <DialogContent><DialogHeader><DialogTitle>Create Delegation</DialogTitle><DialogDescription>Grant someone permission to act on your behalf</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <Input placeholder="Delegate email or user ID" value={delegationEmail} onChange={(e) => setDelegationEmail(e.target.value)} data-testid="delegation-email" />
            <div className="space-y-2"><p className="text-sm font-medium">Access Profile</p>
              {accessProfiles.filter(p => !p.is_system || p.access_profile_id !== 'admin').map(profile => <label key={profile.access_profile_id} className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={delegationProfileIds.includes(profile.access_profile_id)} onCheckedChange={(checked) => setDelegationProfileIds(prev => checked ? [...prev, profile.access_profile_id] : prev.filter(s => s !== profile.access_profile_id))} data-testid={`delegation-profile-${profile.access_profile_id}`} />{profile.label || profile.profile_name || profile.access_profile_id}</label>)}
              {accessProfiles.length === 0 && <p className="text-sm text-muted-foreground">No profiles available</p>}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCreateDelegation(false)}>Cancel</Button><Button onClick={createDelegation} data-testid="confirm-delegation">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Preview Dialog */}
      <Dialog open={showImportDialog} onOpenChange={open => { if (!open) { setShowImportDialog(false); setImportPreview(null); } }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-sm">Import People — Preview</DialogTitle>
            <DialogDescription>{importPreview?.length || 0} records found. Matches by email — existing records will be updated, new ones created.</DialogDescription>
          </DialogHeader>
          <div className="overflow-auto flex-1 border rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-muted/50 sticky top-0 z-10">
                <tr>
                  {importPreview?.[0] && Object.keys(importPreview[0]).slice(0, 6).map(h => (
                    <th key={h} className="p-2 text-left font-medium text-muted-foreground">{h}</th>
                  ))}
                  {importPreview?.[0] && Object.keys(importPreview[0]).length > 6 && <th className="p-2 text-muted-foreground">+{Object.keys(importPreview[0]).length - 6} more</th>}
                </tr>
              </thead>
              <tbody>
                {(importPreview || []).slice(0, 20).map((row, i) => (
                  <tr key={i} className="border-t hover:bg-muted/10">
                    {Object.values(row).slice(0, 6).map((v, j) => (
                      <td key={j} className="p-2 max-w-[140px] truncate">{String(v || '')}</td>
                    ))}
                    {Object.values(row).length > 6 && <td className="p-2 text-muted-foreground">...</td>}
                  </tr>
                ))}
              </tbody>
            </table>
            {(importPreview?.length || 0) > 20 && <div className="p-2 text-xs text-muted-foreground text-center border-t">Showing 20 of {importPreview.length}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => { setShowImportDialog(false); setImportPreview(null); }}>Cancel</Button>
            <Button size="sm" onClick={handleConfirmImport} disabled={importLoading} data-testid="confirm-import-btn">
              {importLoading ? 'Importing...' : `Import ${importPreview?.length || 0} Records`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog open={!!changeRolePerson} onOpenChange={open => { if (!open) setChangeRolePerson(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm">Promote / Change Role</DialogTitle>
            <DialogDescription>{changeRolePerson?.name}{changeRolePerson?.job_title ? ` — currently ${changeRolePerson.job_title}` : ''}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-[11px] font-medium text-muted-foreground mb-1 block">New Role</label>
              <Input value={changeRoleForm.new_role} onChange={e => setChangeRoleForm(f => ({ ...f, new_role: e.target.value }))} placeholder="e.g. Senior Engineer, Head of Product" className="h-8 text-sm" data-testid="new-role-input" />
            </div>
            <div>
              <label className="text-[11px] font-medium text-muted-foreground mb-1 block">New Department (optional)</label>
              <Select value={changeRoleForm.new_department || '__none__'} onValueChange={v => setChangeRoleForm(f => ({ ...f, new_department: v === '__none__' ? '' : v }))}>
                <SelectTrigger className="h-8 text-sm" data-testid="new-dept-input"><SelectValue placeholder="Select department" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No change</SelectItem>
                  {departments.map(d => <SelectItem key={d.department_id || d.name} value={d.name}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Effective Date</label>
              <Input type="date" value={changeRoleForm.effective_date} onChange={e => setChangeRoleForm(f => ({ ...f, effective_date: e.target.value }))} className="h-8 text-sm" data-testid="effective-date-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setChangeRolePerson(null)}>Cancel</Button>
            <Button size="sm" onClick={handleChangeRole} disabled={!changeRoleForm.new_role.trim()} data-testid="confirm-role-change-btn">Change Role</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!memberToReset} onOpenChange={open => { if (!open && !resettingMember) setMemberToReset(null); }}>
        <DialogContent className="max-w-lg" data-testid="reset-member-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
              <RotateCcw className="h-5 w-5" />
              Reset account
            </DialogTitle>
            <DialogDescription>
              Wipes Orka's memory of <span className="font-semibold">{memberToReset?.name || memberToReset?.email}</span> so they can be re-invited as a brand-new user.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-md border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/30 p-3 text-amber-900 dark:text-amber-200 text-xs leading-relaxed">
              This removes their membership, all access grants, prior invitations, AI memory, personalization, and (if no other org keeps them) the user and person records too.
              <br /><br />
              <span className="font-semibold">Tasks owned by this person are kept</span> — only their identity is wiped. They will need to accept a new invite to regain access.
            </div>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={resetWithInvite}
                onChange={e => setResetWithInvite(e.target.checked)}
                className="mt-0.5 rounded"
                data-testid="reset-with-invite-checkbox"
              />
              <span>
                Send a fresh invite to <span className="font-mono">{memberToReset?.email}</span> immediately after reset
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMemberToReset(null)} disabled={resettingMember}>Cancel</Button>
            <Button
              onClick={confirmResetMember}
              disabled={resettingMember}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="confirm-reset-member"
            >
              {resettingMember ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Resetting…</> : 'Reset account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default OrganisationPage;
